import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  api, ApiError, STAGES, STAGE_LABELS,
  type AdsUrlSuffixStatus, type Connection, type Ga4EventMap, type MappingPresetHit, type MoneyColumn, type MysqlEventMap,
  type PosthogEventMap,
  type MongoEventMap, type PostgresEventMap,
  type ProductRow, type RevenueBasis, type Stage, type SuggestMappingsResult, type TrackingKey,
  type UnresolvedMoneyTable,
} from "../api";
import { go, useRoute } from "../router";
import { useAsync } from "../useAsync";
import { StatusChip, type ChipKind } from "../components/StatusChip";
import { shortTime, DASH } from "../format";
import { MysqlCredentialsForm, PostgresCredentialsForm, MongoCredentialsForm, GrantAccessInstead, WebsiteDetector,
  MetaAdsConnectForm, LinkedinAdsConnectForm, MetaSocialConnectForm, XSocialConnectForm,
  StripeCredentialsForm } from "../components/connect/forms";
import { IntegrationMark } from "../components/IntegrationMark";
import { adsOffered, gscOauthOffered } from "../lib/flags";
import { deployGtmTag, GTM_DEPLOY_CONFIRM } from "../lib/gtmDeploy";

/**
 * Integrations answers "is this source wired up?" — distinct from Health, which
 * answers "is data flowing?". A connection can be `active` and still be silent.
 */

type KindMeta = {
  label: string;
  category: string;
  blurb: string;
  pulls: string[];
};

const KINDS: Record<string, KindMeta> = {
  mysql: {
    label: "MySQL",
    category: "Conversions & revenue",
    blurb: "Turn rows from your existing database into a joined customer journey.",
    pulls: [
      "Mapped funnel events from your source tables",
      "Customer identity and attribution fields",
      "Transaction values in their source currency",
    ],
  },
  postgres: {
    label: "Postgres",
    category: "Conversions & revenue",
    blurb: "Turn rows from your Postgres database into a joined customer journey, schema-aware.",
    pulls: [
      "Mapped funnel events from your source tables (any schema)",
      "Customer identity and attribution columns",
      "Transaction values in their source currency",
    ],
  },
  mongo: {
    label: "MongoDB",
    category: "Conversions & revenue",
    blurb: "Turn documents from your MongoDB collections into a joined customer journey.",
    pulls: [
      "Mapped funnel events from your source collections",
      "Customer identity and attribution field paths (dot notation)",
      "Transaction values in their source currency",
    ],
  },
  ga4: {
    label: "Google Analytics 4",
    category: "Traffic & behaviour",
    blurb: "See how people arrive, engage and move through your measured funnel.",
    pulls: [
      "Sessions and engaged sessions by channel",
      "Landing-page traffic and conversions",
      "Mapped GA4 events as funnel stages",
    ],
  },
  gtm: {
    label: "Google Tag Manager",
    category: "Tracking quality",
    blurb: "Audit the live container so broken analytics tags do not silently spoil attribution.",
    pulls: [
      "GA4 tag presence in the live container",
      "Measurement IDs found versus expected",
      "Paused-tag status for Keeper alerts",
    ],
  },
  meta_ads: {
    label: "Meta Ads",
    category: "Paid acquisition",
    blurb: "Campaign spend from your Meta ad account — subject to FunnelKeeper's policy walls.",
    pulls: [
      "Daily campaign, ad-set and ad spend",
      "Campaign names, impressions and clicks",
      "Ad account identifiers",
    ],
  },
  linkedin_ads: {
    label: "LinkedIn Ads",
    category: "Paid acquisition",
    blurb: "Campaign spend from LinkedIn Campaign Manager so B2B paid sits next to Google and Meta.",
    pulls: [
      "Daily campaign spend",
      "Campaign group and creative grain",
      "Spend in the ad account's native currency",
    ],
  },
  meta_social: {
    label: "Meta Social",
    category: "Organic social",
    blurb: "Facebook Page and Instagram Business audience, reach, and the posts that earned it.",
    pulls: [
      "Daily follower, impression and engagement snapshots",
      "Recent Page and Instagram posts",
      "Engagement rate per post",
    ],
  },
  x_social: {
    label: "X (Twitter)",
    category: "Organic social",
    blurb: "Public audience and recent posts from an X account — followers, likes, replies, retweets.",
    pulls: [
      "Daily follower snapshot",
      "Recent tweets with public metrics",
      "Engagement rate when impressions are available",
    ],
  },
  google_ads: {
    label: "Google Ads",
    category: "Paid acquisition",
    blurb: "Bring campaign, keyword and creative spend alongside conversions so each row has a real ROAS.",
    pulls: [
      "Daily spend by campaign, ad group, keyword and creative",
      "Campaign names, impressions and clicks",
      "Spend in the ad account's native currency",
    ],
  },
  semrush: {
    label: "SEMrush",
    category: "Search visibility",
    blurb: "Take a weekly search visibility snapshot without wasting API units.",
    pulls: [
      "Domain rank and organic keyword count",
      "Estimated organic traffic and traffic cost",
      "Paid keyword, traffic and cost estimates",
    ],
  },
  clarity: {
    label: "Microsoft Clarity",
    category: "Session recordings",
    blurb: "Watch how people actually use the page — rage clicks, dead clicks, and the recording that shows why.",
    pulls: [
      "Session recording playback links (opened in Clarity)",
      "Rage-click and dead-click counts by landing page",
      "Script-error signals on the pages that convert",
    ],
  },
  posthog: {
    label: "PostHog",
    category: "Product analytics",
    blurb: "Your product analytics as the funnel: sessions and mapped events, plus the replay behind them.",
    pulls: [
      "Sessions and engaged sessions by channel",
      "Mapped PostHog events as funnel stages",
      "Session replay links and rage/dead-click counts",
    ],
  },
  opinly: {
    label: "Opinly",
    category: "SEO & AI visibility",
    blurb: "See how you show up in search and in AI answers — mention rate, keyword ranks, site audit, competitor gaps.",
    pulls: [
      "LLM / GEO visibility and mention rate (ChatGPT, Gemini, Claude)",
      "Tracked keyword ranks and unranked opportunities",
      "Site-audit health and competitor citation gaps",
    ],
  },
  gsc: {
    label: "Search Console",
    category: "Search demand",
    blurb: "See the queries and pages that earn impressions and clicks — the top of the demand funnel.",
    pulls: [
      "Daily search queries with clicks, impressions, CTR and position",
      "Landing-page grain for the same window",
      "Up to 16 months of history once the backfill ladder finishes",
    ],
  },
  bing: {
    label: "Bing Webmaster",
    category: "Search demand",
    blurb: "Microsoft search queries and page stats alongside Google — same demand surface, a second engine.",
    pulls: [
      "Query and page stats (clicks, impressions, position)",
      "Recent-window history from the Bing Webmaster API",
    ],
  },
  ahrefs: {
    label: "Ahrefs",
    category: "Search visibility",
    blurb: "Weekly domain rating, referring domains and organic keyword ranks — units deplete, so we never sync on a button.",
    pulls: [
      "Domain rating and referring-domain count",
      "Top organic keyword ranks",
    ],
  },
  stripe: {
    label: "Stripe",
    category: "Conversions & revenue",
    blurb: "Successful charges become your bottom-of-funnel conversion and the revenue ledger — refunds and disputes net out.",
    pulls: [
      "Succeeded charges as converted or payment events",
      "Revenue in the charge currency, stitched to the Stripe customer",
      "Refunds and open/lost disputes as negative ledger rows",
      "Attribution from charge metadata (utm_*, gclid, fbclid)",
    ],
  },
  tracking: {
    label: "Event tracking",
    category: "First-party events",
    blurb: "Push funnel events from your site or backend. Same warehouse as MySQL and GA4.",
    pulls: [
      "Browser snippet and GTM Custom HTML",
      "Server-side events with the account API key",
      "Identity and first/last-touch attribution",
    ],
  },
};

const CATALOG_GROUPS: Array<{ category: string; kinds: string[] }> = [
  { category: "First-party events", kinds: ["tracking"] },
  { category: "Conversions & revenue", kinds: ["stripe", "mysql", "postgres", "mongo"] },
  { category: "Traffic & behaviour", kinds: ["ga4", "gtm", "posthog"] },
  { category: "Session recordings", kinds: ["clarity"] },
  { category: "Paid acquisition", kinds: ["google_ads", "meta_ads", "linkedin_ads"] },
  { category: "Organic social", kinds: ["meta_social", "x_social"] },
  { category: "Search demand", kinds: ["gsc", "bing"] },
  { category: "Search visibility", kinds: ["opinly", "semrush", "ahrefs"] },
];

const ANALYTICS_KINDS = ["ga4", "posthog"];
const REVENUE_KINDS = ["stripe", "mysql", "postgres", "mongo"];

// What a tenant can wire themselves. google_ads is conditional on adsOffered
// — see web/src/lib/flags.ts.
const TENANT_KINDS = ["mysql", "postgres", "mongo", "stripe", "ga4", "gtm", "google_ads", "meta_ads", "linkedin_ads", "meta_social", "x_social", "gsc", "bing", "semrush", "ahrefs", "clarity", "posthog", "opinly"]
  .filter((kind) => kind !== "google_ads" || adsOffered);

// Sources a human may run by hand — mirrors SYNCABLE_KINDS on the API. The
// quota-bound ones are deliberately absent: SEMrush/Ahrefs units deplete and
// Clarity allows ten Data Export calls per project per day. gtm is here: its
// tag audit is one unmetered read of the live container. gsc/bing/opinly are
// rate-limited, not metered.
const SYNCABLE_KINDS = ["mysql", "postgres", "mongo", "ga4", "posthog", "gtm", "opinly", "gsc", "bing", "google_ads", "meta_ads", "linkedin_ads", "meta_social", "x_social", "stripe"];

function googleOAuthKind(kind: string): boolean {
  if (kind === "ga4" || kind === "gtm" || (kind === "google_ads" && adsOffered)) return true;
  return kind === "gsc" && gscOauthOffered;
}

function googleSaKind(kind: string): kind is "ga4" | "gtm" | "gsc" {
  return kind === "ga4" || kind === "gtm" || kind === "gsc";
}

/** A triggered sync finishes seconds to minutes later and reports itself
 *  through the connection row, so refresh the card as it lands. */
function followSync(reload: () => void): void {
  for (const ms of [4000, 12_000, 30_000]) setTimeout(reload, ms);
}

function kindMeta(kind: string): KindMeta {
  return KINDS[kind] ?? {
    label: kind,
    category: "Data source",
    blurb: "Bring this source into FunnelKeeper.",
    pulls: [],
  };
}

export function Integrations({ product }: { product: ProductRow | null }) {
  const route = useRoute();
  const conns = useAsync(() => api.connections(), []);
  const tracking = useAsync(
    () => (product ? api.trackingKey(product.slug) : Promise.resolve(null)),
    [product?.slug],
  );
  const openKind = route.name === "integrations" ? route.kind : undefined;
  const open = (kind: string) => go({ name: "integrations", kind });
  const close = () => go({ name: "integrations" });

  if (conns.error) return <Problem message={conns.error} />;
  if (!conns.data) return <Loading />;
  if (!product) {
    return (
      <div className="page integrations-page" style={{ paddingTop: 34 }}>
        <h1>Integrations</h1>
        <p className="sub">Create a project first — sources are wired per project.</p>
      </div>
    );
  }

  const slug = product.slug;
  const rows = conns.data.filter((c) =>
    c.product_slug === slug && (c.kind !== "google_ads" || adsOffered),
  );
  const expected = TENANT_KINDS;
  const connected = rows.filter((c) => c.status === "active");
  const needsAttention = rows.filter((c) => c.status !== "active");
  const connectedKinds = new Set(rows.map((c) => c.kind));
  const hasTrackingKey = Boolean(tracking.data?.key);
  const missing = expected.filter((kind) => !connectedKinds.has(kind));

  const recommended = recommendKinds(missing, hasTrackingKey);
  const catalogKinds = new Set<string>([
    ...missing,
    ...(hasTrackingKey ? [] : ["tracking"]),
  ]);
  const moreKinds = [...catalogKinds].filter((kind) => !recommended.includes(kind));

  const drawerConnection = openKind && openKind !== "tracking"
    ? rows.find((c) => c.kind === openKind) ?? null
    : null;

  return (
    <div className="page integrations-page" style={{ paddingTop: 34 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <h1>Integrations</h1>
        <span className="sub" style={{ maxWidth: 680 }}>
          Sources for <strong>{product.name}</strong>. Credentials stay encrypted
          and are never shown here.
        </span>
      </div>

      {needsAttention.length > 0 && (
        <section className="integration-section">
          <div className="integration-section-head">
            <div>
              <h2>Needs attention</h2>
              <span className="sub">Broken or unfinished connections on this product.</span>
            </div>
            <span className="integration-count num">{needsAttention.length}</span>
          </div>
          <div className="integration-rows">
            {needsAttention.map((c) => (
              <SourceRow
                key={c.id}
                kind={c.kind}
                title={kindMeta(c.kind).label}
                metric={attentionLine(c)}
                chip={{
                  kind: c.syncing ? "pre-gate" : statusChip(c.status),
                  label: c.syncing ? "syncing" : c.status,
                }}
                attention
                onOpen={() => open(c.kind)}
              />
            ))}
          </div>
        </section>
      )}

      <section className="integration-section">
        <div className="integration-section-head">
          <div>
            <h2>Connected</h2>
            <span className="sub">Active sources feeding this product.</span>
          </div>
          <span className="integration-count num">{connected.length + (hasTrackingKey ? 1 : 0)}</span>
        </div>
        {connected.length === 0 && !hasTrackingKey ? (
          <div className="integration-empty">Nothing connected yet. Start with a source below.</div>
        ) : (
          <div className="integration-rows">
            {hasTrackingKey && (
              <SourceRow
                kind="tracking"
                title="Event tracking"
                metric="Write key live"
                chip={{ kind: "passing", label: "key live" }}
                onOpen={() => open("tracking")}
              />
            )}
            {connected.map((c) => (
              <SourceRow
                key={c.id}
                kind={c.kind}
                title={kindMeta(c.kind).label}
                metric={
                  c.syncing
                    ? (c.sync_phase ?? "Syncing now")
                    : [
                        c.last_sync_at ? `Last sync ${shortTime(c.last_sync_at)}` : "Never synced",
                        historyDepth(c),
                      ].filter(Boolean).join(" · ")
                }
                chip={{
                  kind: c.syncing ? "pre-gate" : "passing",
                  label: c.syncing ? "syncing" : c.status,
                }}
                onOpen={() => open(c.kind)}
              />
            ))}
          </div>
        )}
      </section>

      <section className="integration-section">
        <div className="integration-section-head">
          <div>
            <h2>Add a source</h2>
            <span className="sub">
              Recommended first, then the rest of the catalog for this product.
            </span>
          </div>
          <span className="integration-count num">{catalogKinds.size}</span>
        </div>

        <details className="integration-scan">
          <summary>Scan the site to see what is already installed</summary>
          <WebsiteDetector
            slug={slug}
            defaultUrl={product.domain ?? ""}
            compact
            onConnected={conns.reload}
            title="Scan website"
            subtitle="Detect analytics, ads pixels, replay tools and payments already on the page."
          />
        </details>

        {recommended.length > 0 && (
          <div className="integration-recommend">
            <span className="eyebrow">Start here</span>
            <div className="integration-catalog">
              {recommended.map((kind) => (
                <CatalogTile key={kind} kind={kind} recommended onOpen={() => open(kind)} />
              ))}
            </div>
          </div>
        )}

        {moreKinds.length > 0 && (
          <div className="integration-catalog-groups">
            {CATALOG_GROUPS.map((group) => {
              const kinds = group.kinds.filter((kind) => moreKinds.includes(kind));
              if (kinds.length === 0) return null;
              return (
                <div key={group.category} className="integration-catalog-group">
                  <span className="eyebrow">{group.category}</span>
                  <div className="integration-catalog">
                    {kinds.map((kind) => (
                      <CatalogTile key={kind} kind={kind} onOpen={() => open(kind)} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {catalogKinds.size === 0 && (
          <div className="integration-empty">Every available source is connected.</div>
        )}
      </section>

      <div className="banner" style={{ marginTop: 30 }}>
        <span>
          A connection can read <strong>active</strong> and still deliver nothing — that is what
          the Health tab is for.
        </span>
      </div>

      {openKind && (openKind === "tracking" || openKind in KINDS) && (
        <SourceDrawer
          kind={openKind}
          slug={slug}
          productName={product.name}
          connection={drawerConnection}
          onClose={close}
          reload={() => { conns.reload(); tracking.reload(); }}
        />
      )}
    </div>
  );
}

function recommendKinds(missing: string[], hasTrackingKey: boolean): string[] {
  const out: string[] = [];
  if (ANALYTICS_KINDS.every((kind) => missing.includes(kind))) {
    out.push("ga4", "posthog");
  }
  if (!hasTrackingKey) out.push("tracking");
  if (REVENUE_KINDS.every((kind) => missing.includes(kind))) out.push("stripe");
  if (missing.includes("gsc") && out.length < 4) out.push("gsc");
  return out.slice(0, 4);
}

function attentionLine(c: Connection): string {
  if (c.last_error) return c.last_error;
  if (c.syncing) return c.sync_phase ?? "Syncing now";
  return "Needs a next step — open to finish setup.";
}

function SourceRow({
  kind,
  title,
  metric,
  chip,
  attention,
  onOpen,
}: {
  kind: string;
  title: string;
  metric: string;
  chip: { kind: ChipKind; label: string };
  attention?: boolean;
  onOpen: () => void;
}) {
  return (
    <button
      type="button"
      className={`integration-row${attention ? " integration-row-attention" : ""}`}
      onClick={onOpen}
    >
      <IntegrationMark kind={kind} size={18} />
      <div className="integration-row-copy">
        <span className="integration-row-title">{title}</span>
        <span className="integration-row-metric">{metric}</span>
      </div>
      <StatusChip kind={chip.kind} label={chip.label} />
      <span className="integration-row-chevron" aria-hidden>›</span>
    </button>
  );
}

function CatalogTile({
  kind,
  recommended,
  onOpen,
}: {
  kind: string;
  recommended?: boolean;
  onOpen: () => void;
}) {
  const meta = kindMeta(kind);
  return (
    <button
      type="button"
      className={`integration-tile${recommended ? " integration-tile-rec" : ""}`}
      onClick={onOpen}
    >
      <IntegrationMark kind={kind} size={18} />
      <span className="integration-tile-title">{meta.label}</span>
      <span className="integration-tile-blurb">{meta.blurb}</span>
    </button>
  );
}

function SourceDrawer({
  kind,
  slug,
  productName,
  connection,
  onClose,
  reload,
}: {
  kind: string;
  slug: string;
  productName: string;
  connection: Connection | null;
  onClose: () => void;
  reload: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const meta = kindMeta(kind);
  return (
    <div className="integration-drawer-overlay" onClick={onClose} role="presentation">
      <aside
        className="integration-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="integration-drawer-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="integration-drawer-head">
          <IntegrationMark kind={kind} />
          <div className="integration-drawer-heading">
            <span className="eyebrow">{meta.category}</span>
            <h2 id="integration-drawer-title">{meta.label}</h2>
          </div>
          {kind === "tracking" ? (
            <StatusChip kind="pre-gate" label="first-party" />
          ) : (
            <StatusChip
              kind={connection ? (connection.syncing ? "pre-gate" : statusChip(connection.status)) : "pre-gate"}
              label={connection ? (connection.syncing ? "syncing" : connection.status) : "not connected"}
            />
          )}
          <button type="button" className="btn-text" onClick={onClose}>Close</button>
        </header>
        <div className="integration-drawer-body">
          {kind === "tracking" ? (
            <TrackingPanel slug={slug} productName={productName} />
          ) : connection ? (
            <ConnectionPanel c={connection} reload={reload} />
          ) : (
            <MissingPanel kind={kind} slug={slug} productName={productName} reload={reload} />
          )}
        </div>
      </aside>
    </div>
  );
}

function CopyBlock({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span className="eyebrow">{label}</span>
        <span style={{ flex: 1 }} />
        <button
          type="button"
          className="btn-text"
          onClick={() => {
            void navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}
        >
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <pre
        className="num"
        style={{
          margin: 0,
          padding: "10px 12px",
          fontSize: 12,
          lineHeight: 1.5,
          whiteSpace: "pre-wrap",
          wordBreak: "break-all",
          background: "var(--paper-2, transparent)",
          border: "1px solid var(--border-hairline)",
          borderRadius: 6,
        }}
      >
        {value}
      </pre>
    </div>
  );
}

function TrackingPanel({ slug, productName }: { slug: string; productName: string }) {
  const info = useAsync(() => api.trackingKey(slug), [slug]);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [data, setData] = useState<TrackingKey | null>(null);
  const current = data ?? info.data;

  const run = async (label: string, fn: () => Promise<string>) => {
    setBusy(label);
    setNote(null);
    try { setNote(await fn()); }
    catch (e) { setNote(e instanceof ApiError ? e.message : String(e)); }
    finally { setBusy(null); }
  };

  const mint = () => run("mint", async () => {
    const res = await api.rotateTrackingKey(slug);
    const next = await api.trackingKey(slug);
    setData(next);
    return res.rotated ? "Key rotated. Update the snippet on your site." : "Key created.";
  });

  return (
    <div className="panel integration-card">
      <div className="integration-card-head">
        <IntegrationMark kind="tracking" />
        <div className="integration-card-heading">
          <span className="eyebrow">{productName} · First-party events</span>
          <span className="integration-card-title">Tracking</span>
        </div>
        <StatusChip
          kind={current?.key ? "passing" : "pre-gate"}
          label={current?.key ? "key live" : "no key"}
        />
      </div>
      <p className="integration-blurb">
        Drop the snippet on every page, or POST events from your backend with the account API key.
        Stage is the funnel step; name is free-form.
      </p>

      {info.loading && !current && <span className="sub">Loading key…</span>}
      {info.error && <div className="integration-error">{info.error}</div>}

      {current?.key && current.snippet && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 8 }}>
          <CopyBlock label="Browser snippet" value={current.snippet} />
          {current.gtm_html && (
            <CopyBlock label="GTM Custom HTML (All Pages trigger)" value={current.gtm_html} />
          )}
          <CopyBlock label="Server-side (account API key)" value={current.curl_example} />
        </div>
      )}

      {current?.gtm_deploy?.fk_snippet_found && (
        <span className="sub" style={{ marginTop: 8 }}>Snippet detected in the live GTM container.</span>
      )}

      <div className="integration-actions" style={{ marginTop: 14 }}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button type="button" className="btn-ghost" disabled={busy !== null} onClick={() => void mint()}>
            {busy === "mint" ? "Working…" : current?.key ? "Rotate key" : "Create key"}
          </button>
          {current?.gtm_connected && (
            <button
              type="button"
              className="btn-solid"
              disabled={busy !== null}
              onClick={() => {
                if (!window.confirm(GTM_DEPLOY_CONFIRM)) return;
                void run("deploy", async () => {
                  const note = await deployGtmTag(slug, Boolean(current.key));
                  setData(await api.trackingKey(slug));
                  return note;
                });
              }}
            >
              {busy === "deploy" ? "Publishing…" : "Deploy via Tag Manager"}
            </button>
          )}
        </div>
        {note && <div style={{ fontSize: 12.5, color: "var(--text-3)", marginTop: 8 }}>{note}</div>}
      </div>
    </div>
  );
}

function ConnectionPanel({ c, reload }: { c: Connection; reload: () => void }) {
  const meta = kindMeta(c.kind);
  const next = nextStep(c);

  return (
    <div className="panel integration-card">
      <div className="integration-card-head">
        <IntegrationMark kind={c.kind} />
        <div className="integration-card-heading">
          <span className="eyebrow">{c.product_name || c.product_slug} · {meta.category}</span>
          <span className="integration-card-title">{meta.label}</span>
        </div>
        <StatusChip kind={c.syncing ? "pre-gate" : statusChip(c.status)}
                    label={c.syncing ? "syncing" : c.status} />
      </div>

      <p className="integration-blurb">{meta.blurb}</p>
      {/* Mid-run, what it is doing beats when it last finished — "Never synced"
          under a spinner is the exact sentence that reads as broken. */}
      {c.syncing ? (
        <span className="integration-sync" style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--keeper)" }}>
          <span className="sync-banner-spinner" aria-hidden />
          {c.sync_phase ?? "Syncing now"}
        </span>
      ) : (
        <span className="integration-sync num">
          {c.last_sync_at ? `Last sync ${shortTime(c.last_sync_at)}` : "Never synced"}
          {historyDepth(c) && ` · ${historyDepth(c)}`}
        </span>
      )}

      <PullList pulls={meta.pulls} />

      <details className="integration-details">
        <summary>Connection details</summary>
        <dl>
          <dt>Secret ref</dt>
          <dd className="num">{c.secret_ref ?? DASH}</dd>
          <dt>Config</dt>
          <dd><ConfigView config={c.config} /></dd>
        </dl>
      </details>

      {c.last_error && (
        <div className="integration-error">
          {c.last_error}
        </div>
      )}

      {next && (
        <div
          style={{
            marginTop: 12,
            paddingTop: 12,
            borderTop: "1px solid var(--border-hairline)",
            fontSize: 12.5,
            lineHeight: 1.6,
            color: "var(--text-3)",
          }}
        >
          <span className="eyebrow" style={{ display: "block", marginBottom: 6 }}>
            Next step
          </span>
          {next}
        </div>
      )}

      <ConnectionActions c={c} reload={reload} />
      {c.kind === "meta_ads" && <MetaCampaignFilterEditor c={c} reload={reload} />}
      {c.kind === "gtm" && <GtmTagPanel c={c} />}
      {c.kind === "google_ads" && <AdsUrlSuffixPanel c={c} />}
      {c.kind === "ga4" && <Ga4EventMapEditor c={c} reload={reload} />}
      {c.kind === "posthog" && <PosthogEventMapEditor c={c} reload={reload} />}
      {c.kind === "mysql" && <MysqlEventMapEditor c={c} reload={reload} />}
      {c.kind === "postgres" && <PostgresEventMapEditor c={c} reload={reload} />}
      {c.kind === "mongo" && <MongoEventMapEditor c={c} reload={reload} />}
    </div>
  );
}

function PullList({ pulls }: { pulls: string[] }) {
  if (pulls.length === 0) return null;
  return (
    <div className="integration-pulls">
      <span className="eyebrow">Data we pull</span>
      <ul>
        {pulls.map((item) => (
          <li key={item}><span aria-hidden="true" />{item}</li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The tag, on the card where the container was just connected. Event tracking
 * above owns the key; publishing FunnelKeeper's tag into the container is the
 * one action that belongs to GTM, and this is where a human looks for it right
 * after connecting — so it is offered in both places.
 */
function GtmTagPanel({ c }: { c: Connection }) {
  const ready = c.status === "active" && Boolean(c.config?.container_path);
  const info = useAsync(
    () => ready ? api.trackingKey(c.product_slug) : Promise.resolve(null),
    [c.product_slug, c.id, ready],
  );
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [data, setData] = useState<TrackingKey | null>(null);
  const current = data ?? info.data;

  if (!ready) return null;

  const deploy = async () => {
    if (!window.confirm(GTM_DEPLOY_CONFIRM)) return;
    setBusy(true);
    setNote(null);
    try {
      const said = await deployGtmTag(c.product_slug, Boolean(current?.key));
      setData(await api.trackingKey(c.product_slug));
      setNote(said);
    } catch (e) {
      setNote(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const deployed = current?.gtm_deploy;
  const live = deployed?.fk_snippet_found;

  return (
    <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--border-hairline)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span className="eyebrow">FunnelKeeper tag</span>
        {current && (
          <span style={{ fontSize: 12, color: live ? "var(--pos)" : "var(--text-faint)" }}>
            {live ? "in the live container" : "not in the container"}
          </span>
        )}
      </div>
      <p className="integration-blurb" style={{ marginTop: 8 }}>
        Publish the event snippet straight into this container — no copy-paste, no developer. It
        goes in as a new workspace version and is published on confirm.
        {!current?.key && " A tracking key is created with it."}
      </p>

      {info.loading && !current && <span className="sub">Checking the container…</span>}
      {info.error && <div className="integration-error">{info.error}</div>}

      {deployed?.deployed_at && (
        <span className="integration-sync num">
          Last published {shortTime(deployed.deployed_at)}
          {deployed.version_name && ` · ${deployed.version_name}`}
        </span>
      )}

      <div className="integration-actions" style={{ marginTop: 14 }}>
        <button type="button" className="btn-solid" disabled={busy || !current} onClick={() => void deploy()}>
          {busy ? "Publishing…" : live ? "Republish tag" : "Deploy tag to container"}
        </button>
        {note && <div style={{ fontSize: 12.5, color: "var(--text-3)", marginTop: 8 }}>{note}</div>}
      </div>
    </div>
  );
}

/**
 * ValueTrack params on the account final URL suffix. Without them, keyword and
 * creative ROI depend on a 90-day gclid sweep. Confirm required — this changes
 * every ad's landing URL. Existing tenant params are kept (merge, never replace).
 */
function AdsUrlSuffixPanel({ c }: { c: Connection }) {
  const hasAccount = Boolean(c.config?.customer_id);
  const info = useAsync(
    () => hasAccount ? api.adsUrlSuffix.get(c.product_slug) : Promise.resolve(null),
    [c.product_slug, c.id, hasAccount],
  );
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [data, setData] = useState<AdsUrlSuffixStatus | null>(null);
  const current = data ?? info.data;

  if (!hasAccount) return null;

  const apply = async () => {
    if (!window.confirm(
      "This adds FunnelKeeper's tracking parameters to the Google Ads account final URL suffix. It changes the landing URL of every ad. Existing parameters are kept. Continue?",
    )) return;
    setBusy(true);
    setNote(null);
    try {
      const res = await api.adsUrlSuffix.deploy(c.product_slug);
      setData(await api.adsUrlSuffix.get(c.product_slug));
      setNote(res.already_complete
        ? "Nothing to add — every parameter was already there."
        : `Added ${res.added.join(", ")}.`);
    } catch (e) {
      setNote(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--border-hairline)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span className="eyebrow">Landing URL parameters</span>
        {current && !current.read_error && (
          <span style={{ fontSize: 12, color: current.complete ? "var(--pos)" : "var(--text-faint)" }}>
            {current.complete ? "all present" : `${current.missing.length} missing`}
          </span>
        )}
      </div>
      <p className="integration-blurb" style={{ marginTop: 8 }}>
        Keyword and creative ROI is exact when every click names the ad that produced it.
        We add those ids to the account suffix and leave anything already there alone.
      </p>

      {info.loading && !current && <span className="sub">Checking the account suffix…</span>}
      {info.error && <div className="integration-error">{info.error}</div>}

      {current && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 8 }}>
          {current.read_error ? (
            <>
              {/* We could not see the account, so we cannot write to it either —
                  hand over the parameters instead of a dead button. */}
              <div className="integration-error">{current.read_error}</div>
              <CopyBlock label="Add these to the account final URL suffix" value={current.recommended_suffix} />
            </>
          ) : (
            <>
              <div style={{ fontSize: 12.5, color: "var(--text-3)", lineHeight: 1.6 }}>
                Auto-tagging is {current.auto_tagging_enabled ? "on" : "off — turn it on in Google Ads so gclid still lands"}.
                {current.deployed_at && <> Last applied {shortTime(current.deployed_at)}.</>}
              </div>
              {current.complete ? (
                <span className="sub">Every parameter FunnelKeeper needs is already on the account.</span>
              ) : (
                <CopyBlock label="Proposed final URL suffix" value={current.proposed_suffix} />
              )}
            </>
          )}
        </div>
      )}

      <div className="integration-actions" style={{ marginTop: 14 }}>
        <button
          type="button"
          className="btn-solid"
          disabled={busy || !current || current.complete || Boolean(current.read_error)}
          onClick={() => void apply()}
        >
          {busy ? "Applying…" : current?.complete ? "Parameters in place" : "Add tracking parameters"}
        </button>
        {note && <div style={{ fontSize: 12.5, color: "var(--text-3)", marginTop: 8 }}>{note}</div>}
      </div>
    </div>
  );
}

// MySQL credentials: one form, used to connect first time (MissingPanel) and
// to replace credentials later. Values go straight to the encrypted credential
// store; nothing here is ever readable back.
const NET_COL = /^(netrevenue|netamount|commission|commissionamount|commissioncents|fee|fees|feeamount|feecents|ourfee|platformfee|servicefee|take|takeamount|margin|grossprofit|profit|earnings|payout|net)$/;
const GROSS_COL = /^(saleprice|soldprice|purchaseprice|listprice|gmv|gross|grossamount|grandtotal|ordertotal|ordervalue|subtotal|price|pricecents|total|totalamount|totalcents)$/;

function looksLikeMoney(column: string): MoneyColumn["looks_like"] {
  const n = column.toLowerCase().replace(/[^a-z0-9]/g, "");
  if (NET_COL.test(n)) return "net";
  if (GROSS_COL.test(n)) return "gross";
  return "unclear";
}

function labelLooksLike(looks: MoneyColumn["looks_like"]): string {
  if (looks === "net") return "your fee";
  if (looks === "gross") return "sale / GMV";
  return "unclear";
}

type RevenueDraft = {
  amount: string;
  currency: string;
  is_dollars?: boolean;
  basis?: RevenueBasis;
};

/** Amount picker that shows fee vs sale-price candidates side by side, plus an
 *  explicit net/gross basis. Without this the amount is a blind text field and
 *  a marketplace books GMV as revenue. */
function RevenueAmountField({
  table,
  value,
  money,
  fieldLabel,
  onChange,
}: {
  table: string;
  value: RevenueDraft;
  money: MoneyColumn[];
  fieldLabel: string;
  onChange: (next: RevenueDraft) => void;
}) {
  const candidates = money.filter((c) => !table || c.table === table);
  const looks = candidates.find((c) => c.column === value.amount)?.looks_like
    ?? (value.amount ? looksLikeMoney(value.amount) : "unclear");
  const overstated = Boolean(value.amount) && looks === "gross" && value.basis !== "net";
  const inList = candidates.some((c) => c.column === value.amount);

  const setAmount = (amount: string) => {
    const hint = candidates.find((c) => c.column === amount)?.looks_like
      ?? (amount ? looksLikeMoney(amount) : "unclear");
    onChange({
      ...value,
      amount,
      basis: hint === "net" || hint === "gross" ? hint : value.basis,
    });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, width: "100%" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        {candidates.length > 0 ? (
          <select
            className="input"
            style={{ minHeight: 34, fontSize: 12.5, minWidth: 220 }}
            value={value.amount}
            aria-label={fieldLabel}
            onChange={(e) => setAmount(e.target.value)}
          >
            <option value="">Pick the money you keep…</option>
            {candidates.map((c) => (
              <option key={`${c.table}.${c.column}`} value={c.column}>
                {c.column} — {labelLooksLike(c.looks_like)}
                {c.sample ? ` (e.g. ${c.sample})` : ""}
              </option>
            ))}
            {value.amount && !inList && (
              <option value={value.amount}>{value.amount} — current</option>
            )}
          </select>
        ) : (
          <input
            className="input"
            style={{ minHeight: 34, fontSize: 12.5, minWidth: 180 }}
            value={value.amount}
            placeholder="fee / commission column"
            aria-label={fieldLabel}
            onChange={(e) => setAmount(e.target.value)}
          />
        )}
        <select
          className="input"
          style={{ minHeight: 34, fontSize: 12.5, width: 150 }}
          value={value.basis ?? ""}
          aria-label="What this amount means"
          onChange={(e) => {
            const basis = e.target.value as RevenueBasis | "";
            onChange({ ...value, basis: basis === "net" || basis === "gross" ? basis : undefined });
          }}
        >
          <option value="">what it means…</option>
          <option value="net">Your take (fee)</option>
          <option value="gross">Full sale / GMV</option>
        </select>
        <input
          className="input"
          style={{ minHeight: 34, fontSize: 12.5, width: 70 }}
          maxLength={3}
          value={value.currency}
          aria-label="Currency"
          onChange={(e) => onChange({ ...value, currency: e.target.value })}
        />
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "var(--text-3)" }}>
          <input
            type="checkbox"
            checked={Boolean(value.is_dollars)}
            onChange={(e) => onChange({ ...value, is_dollars: e.target.checked })}
          />
          stores dollars (not cents)
        </label>
      </div>
      {overstated && (
        <div className="form-error" style={{ fontSize: 12.5 }}>
          This column looks like the sale price, not your fee. Revenue, LTV and payback
          will be overstated. Pick the fee/commission column if you have one — or set
          “Your take” if this really is what you keep.
        </div>
      )}
    </div>
  );
}

function persistRevenue(amountKey: "amount_col" | "amount_field", r: RevenueDraft):
  | { amount_col: string; currency: string; is_dollars?: boolean; basis?: RevenueBasis }
  | { amount_field: string; currency: string; is_dollars?: boolean; basis?: RevenueBasis }
  | undefined {
  if (!r.amount.trim()) return undefined;
  return {
    [amountKey]: r.amount.trim(),
    currency: (r.currency || "AUD").trim().toUpperCase(),
    ...(r.is_dollars ? { is_dollars: true } : {}),
    ...(r.basis === "net" || r.basis === "gross" ? { basis: r.basis } : {}),
  } as { amount_col: string; currency: string; is_dollars?: boolean; basis?: RevenueBasis };
}

function RevenueChoiceAsk({
  tables, maps, onPick,
}: {
  tables: UnresolvedMoneyTable[];
  maps: MysqlEventMap[];
  onPick: (table: string, column: string, basis: RevenueBasis) => void;
}) {
  const relevant = tables.filter((u) => maps.some((m) =>
    m.table === u.table && (m.revenue || m.stage === "converted" || m.stage === "payment")));
  if (relevant.length === 0) return null;
  return (
    <div style={{
      marginTop: 10, padding: "12px 14px", borderRadius: 10,
      border: "1px solid var(--neg, #b42318)", background: "color-mix(in srgb, var(--neg, #b42318) 6%, transparent)",
      display: "flex", flexDirection: "column", gap: 12,
    }}>
      <div>
        <span className="eyebrow">Which amount do you keep?</span>
        <div style={{ fontSize: 12.5, color: "var(--text-3)", marginTop: 4, lineHeight: 1.55 }}>
          This table has more than one price column and the names don’t tell us
          which is money your business earns. A model will not guess — pick one
          before we sync. Sale price / GMV is the wrong answer if you take a fee.
        </div>
      </div>
      {relevant.map((u) => {
        const current = maps.find((m) => m.table === u.table && m.revenue)?.revenue;
        const settled = Boolean(current?.amount_col && current.basis);
        return (
          <div key={u.table} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <span className="num" style={{ fontSize: 12.5 }}>{u.table}{settled ? " — saved" : ""}</span>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {u.columns.map((col) => (
                <div key={col.column} style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span className="num" style={{ fontSize: 12.5, minWidth: 140 }}>{col.column}</span>
                  <span style={{ fontSize: 12, color: "var(--text-faint)" }}>
                    {labelLooksLike(col.looks_like)}{col.sample ? ` · e.g. ${col.sample}` : ""}
                  </span>
                  <button
                    type="button"
                    className={current?.amount_col === col.column && current.basis === "net" ? "btn-solid" : "btn-ghost"}
                    style={{ minHeight: 30, fontSize: 12 }}
                    onClick={() => onPick(u.table, col.column, "net")}
                  >
                    I keep this
                  </button>
                  <button
                    type="button"
                    className={current?.amount_col === col.column && current.basis === "gross" ? "btn-solid" : "btn-ghost"}
                    style={{ minHeight: 30, fontSize: 12 }}
                    onClick={() => onPick(u.table, col.column, "gross")}
                  >
                    Full sale / GMV
                  </button>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const REMAP_NOTE =
  "Saved. A sync is restating the last 90 days. If you changed the revenue column, history is corrected with adjustment rows — originals stay, the difference is a new row.";

// MySQL event maps: the table→stage mapping, editable in place. Each map may
// stitch an identity, resolve a channel (attribution columns), and carry the
// revenue column whose per-row amounts back 'transaction'-mode conversions.
function MysqlEventMapEditor({ c, reload }: { c: Connection; reload: () => void }) {
  const initial = (Array.isArray(c.config?.event_maps) ? c.config!.event_maps : []) as MysqlEventMap[];
  const [maps, setMaps] = useState<MysqlEventMap[]>(() => initial.map((m) => structuredClone(m)));
  const [open, setOpen] = useState(false);
  const [attrOpen, setAttrOpen] = useState<Record<number, boolean>>({});
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [money, setMoney] = useState<MoneyColumn[]>([]);
  const [unresolved, setUnresolved] = useState<UnresolvedMoneyTable[]>([]);

  const patch = (i: number, fn: (m: MysqlEventMap) => MysqlEventMap) =>
    setMaps(maps.map((m, j) => (j === i ? fn(m) : m)));
  // Empty optional strings must not reach the API — identifiers are validated.
  const clean = (m: MysqlEventMap): MysqlEventMap => {
    const trimmed = <T extends Record<string, string | undefined>>(o?: T) => {
      if (!o) return undefined;
      const out: Record<string, string> = {};
      for (const [k, v] of Object.entries(o)) if (v && v.trim()) out[k] = v.trim();
      return Object.keys(out).length > 0 ? (out as T) : undefined;
    };
    return {
      table: m.table.trim(), pk: m.pk.trim(), ts_col: m.ts_col.trim(), stage: m.stage,
      score_col: m.score_col?.trim() || undefined,
      identity: trimmed(m.identity),
      attribution: trimmed(m.attribution),
      revenue: persistRevenue("amount_col", {
        amount: m.revenue?.amount_col ?? "",
        currency: m.revenue?.currency || "AUD",
        is_dollars: m.revenue?.is_dollars,
        basis: m.revenue?.basis,
      }) as MysqlEventMap["revenue"],
    };
  };

  const pendingAsk = unresolved.filter((u) => {
    const rev = maps.find((m) => m.table === u.table && m.revenue);
    if (!rev?.revenue) return false;
    return !rev.revenue.basis || !rev.revenue.amount_col.trim();
  });

  const save = async () => {
    if (pendingAsk.length > 0) {
      setOpen(true);
      setNote(`This table has more than one price column and the names don’t say which is yours: ${pendingAsk.map((u) => u.table).join(", ")}. Pick the amount you keep before saving.`);
      return;
    }
    setBusy(true);
    setNote(null);
    try {
      const body = maps.filter((m) => m.table.trim()).map(clean);
      await api.setMysqlEventMaps(c.product_slug, body);
      setMaps(body);
      reload();
      followSync(reload);
      setNote(REMAP_NOTE);
    } catch (e) {
      setNote(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const applySuggestion = (proposed: MysqlEventMap[], ask?: UnresolvedMoneyTable[]) => {
    if (ask) setUnresolved(ask);
    const askFor = new Set((ask ?? unresolved).map((u) => u.table));
    setMaps(proposed.map((m) => {
      const copy = structuredClone(m);
      // Names and the model couldn't pick uniquely — don't let a guess land
      // as revenue. The ask below is the decision.
      if (copy.revenue && askFor.has(copy.table)) {
        copy.revenue = { ...copy.revenue, amount_col: "", basis: undefined };
      }
      return copy;
    }));
    setOpen(true);
    setNote(askFor.size > 0
      ? "Proposal only — a table has more than one price column and we can’t tell which is yours. Pick the amount you keep below, then Save."
      : "Proposal only — review the maps and Save to apply. Check the revenue column is your fee, not the sale price.");
  };

  const tablesKey = maps.map((m) => m.table.trim()).filter(Boolean).sort().join(",");
  useEffect(() => {
    if (!open && maps.length > 0) return;
    let cancelled = false;
    const tables = tablesKey ? tablesKey.split(",") : undefined;
    void api.suggestMysqlMappings(c.product_slug, { source: "preset", tables }).then(
      (res) => {
        if (cancelled) return;
        setMoney(res.schema_summary.money_columns ?? []);
        setUnresolved(res.schema_summary.unresolved_money_tables ?? []);
      },
      () => { /* schema unreadable — the picker falls back to a text field */ },
    );
    return () => { cancelled = true; };
  }, [c.product_slug, open, tablesKey, maps.length]);

  const smallInput = (value: string | undefined, placeholder: string, onChange: (v: string) => void, label: string) => (
    <input className="input" style={{ minHeight: 34, fontSize: 12.5 }} value={value ?? ""}
           placeholder={placeholder} aria-label={label} onChange={(e) => onChange(e.target.value)} />
  );

  return (
    <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--border-hairline)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span className="eyebrow">Event maps</span>
        <span style={{ fontSize: 12, color: "var(--text-faint)" }}>
          {maps.length === 0 ? "none yet — nothing syncs without them" : `${maps.length} table${maps.length === 1 ? "" : "s"} → stages`}
        </span>
        <span style={{ flex: 1 }} />
        <button type="button" className="btn-text" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
          {open ? "Hide" : "Edit"}
        </button>
      </div>

      {(maps.length === 0 || open) && (
        <SuggestMappingsPanel
          slug={c.product_slug}
          autofetch={maps.length === 0}
          onApply={applySuggestion}
          onMoney={setMoney}
          onUnresolved={setUnresolved}
        />
      )}

      {(open || maps.length === 0) && unresolved.length > 0 && (
        <RevenueChoiceAsk
          tables={unresolved}
          maps={maps}
          onPick={(table, column, basis) => {
            setMaps((prev) => {
              const idx = prev.findIndex((m) => m.table === table && m.revenue);
              if (idx < 0) {
                return [...prev, {
                  table, pk: "id", ts_col: "created_at", stage: "converted",
                  revenue: { amount_col: column, currency: "AUD", is_dollars: true, basis },
                }];
              }
              return prev.map((m, i) => i === idx
                ? { ...m, revenue: { ...m.revenue!, amount_col: column, basis } }
                : m);
            });
            setOpen(true);
          }}
        />
      )}

      {open && (
        <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 12 }}>
          {maps.map((m, i) => (
            <div key={i} style={{ border: "1px solid var(--border-hairline)", borderRadius: 10, padding: "12px 14px",
                                  display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                            gap: 8, alignItems: "center" }}>
                {smallInput(m.table, "table", (v) => patch(i, (x) => ({ ...x, table: v })), `Table for map ${i + 1}`)}
                {smallInput(m.pk, "pk (id)", (v) => patch(i, (x) => ({ ...x, pk: v })), `Primary key for map ${i + 1}`)}
                {smallInput(m.ts_col, "timestamp col", (v) => patch(i, (x) => ({ ...x, ts_col: v })), `Timestamp column for map ${i + 1}`)}
                <select className="input" style={{ minHeight: 34, fontSize: 12.5 }} value={m.stage}
                        aria-label={`Stage for map ${i + 1}`}
                        onChange={(e) => patch(i, (x) => ({ ...x, stage: e.target.value as Stage }))}>
                  {STAGES.map((st) => <option key={st} value={st}>{STAGE_LABELS[st]} ({st})</option>)}
                </select>
                <button type="button" className="wtool wtool-x" title="Remove map"
                        onClick={() => setMaps(maps.filter((_, j) => j !== i))}>×</button>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8 }}>
                {smallInput(m.identity?.email_col, "email column (identity)",
                  (v) => patch(i, (x) => ({ ...x, identity: { ...x.identity, email_col: v } })), `Email column for map ${i + 1}`)}
                {smallInput(m.identity?.external_id_col, "person/listing id column",
                  (v) => patch(i, (x) => ({ ...x, identity: { ...x.identity, external_id_col: v } })), `External id column for map ${i + 1}`)}
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "var(--text-3)" }}>
                    <input type="checkbox" checked={Boolean(m.revenue)}
                           onChange={(e) => patch(i, (x) => ({
                             ...x, revenue: e.target.checked ? { amount_col: "", currency: "AUD", is_dollars: true } : undefined,
                           }))} />
                    Revenue on this table — your fee, not the sale price
                  </label>
                  <span style={{ flex: 1 }} />
                  <button type="button" className="btn-text" aria-expanded={Boolean(attrOpen[i])}
                          onClick={() => setAttrOpen({ ...attrOpen, [i]: !attrOpen[i] })}>
                    {attrOpen[i] ? "Hide attribution columns" : "Attribution columns…"}
                  </button>
                </div>
                {m.revenue && (
                  <RevenueAmountField
                    table={m.table}
                    fieldLabel={`Revenue column for map ${i + 1}`}
                    money={money}
                    value={{
                      amount: m.revenue.amount_col,
                      currency: m.revenue.currency,
                      is_dollars: m.revenue.is_dollars,
                      basis: m.revenue.basis,
                    }}
                    onChange={(rev) => patch(i, (x) => ({
                      ...x,
                      revenue: {
                        amount_col: rev.amount,
                        currency: rev.currency,
                        is_dollars: rev.is_dollars,
                        basis: rev.basis,
                      },
                    }))}
                  />
                )}
              </div>

              {attrOpen[i] && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 8 }}>
                  {([
                    ["utm_source_col", "utm_source col"], ["utm_medium_col", "utm_medium col"],
                    ["utm_campaign_col", "utm_campaign col"], ["gclid_col", "gclid col"],
                    ["fbclid_col", "fbclid col"], ["li_fat_id_col", "li_fat_id col"], ["referrer_col", "referrer col"],
                    ["channel_col", "channel col (canonical)"],
                  ] as const).map(([key, ph]) => (
                    <span key={key}>
                      {smallInput(m.attribution?.[key], ph,
                        (v) => patch(i, (x) => ({ ...x, attribution: { ...x.attribution, [key]: v } })), `${ph} for map ${i + 1}`)}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}

          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <button type="button" className="btn-text" disabled={maps.length >= 20}
                    onClick={() => setMaps([...maps, { table: "", pk: "id", ts_col: "created_at", stage: "lead" }])}>
              + Add a table
            </button>
            <span style={{ flex: 1 }} />
            <button type="button" className="btn-ghost" style={{ minHeight: 36 }} onClick={save} disabled={busy}>
              {busy ? "Saving…" : "Save event maps"}
            </button>
          </div>
          <div style={{ fontSize: 12, color: "var(--text-faint)", lineHeight: 1.55 }}>
            One stage per source (a stage fed twice double-counts), one revenue map per table, and put
            the shared person/listing id on every map so the stages stitch to one identity. The revenue
            column is what YOUR business keeps — a marketplace fee, not the car price. Changing it and
            saving restates every day already backfilled, with adjustment rows.
          </div>
          {note && <div style={{ fontSize: 12.5, color: "var(--text-3)" }}>{note}</div>}
        </div>
      )}
    </div>
  );
}

function summarizeMaps(maps: MysqlEventMap[]): string {
  return maps.map((m) => {
    const rev = m.revenue ? ` (${m.revenue.currency}, ${m.revenue.amount_col})` : "";
    return `${m.table}→${m.stage}${rev}`;
  }).join(" · ");
}

function SuggestMappingsPanel({
  slug, autofetch, onApply, onMoney, onUnresolved,
}: {
  slug: string;
  autofetch: boolean;
  onApply: (maps: MysqlEventMap[], unresolved?: UnresolvedMoneyTable[]) => void;
  onMoney?: (cols: MoneyColumn[]) => void;
  onUnresolved?: (tables: UnresolvedMoneyTable[]) => void;
}) {
  const [presets, setPresets] = useState<MappingPresetHit[] | null>(null);
  const [lastUnresolved, setLastUnresolved] = useState<UnresolvedMoneyTable[]>([]);
  const [tableNames, setTableNames] = useState<string[]>([]);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState<"preset" | "ai" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(autofetch);

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return tableNames;
    return tableNames.filter((n) => n.toLowerCase().includes(q));
  }, [tableNames, filter]);

  const applySchema = (res: SuggestMappingsResult) => {
    setPresets(res.presets ?? []);
    const names = res.schema_summary.table_names ?? [];
    setTableNames(names);
    const unresolved = res.schema_summary.unresolved_money_tables ?? [];
    setLastUnresolved(unresolved);
    onMoney?.(res.schema_summary.money_columns ?? []);
    onUnresolved?.(unresolved);
    setSelected((prev) => {
      if (prev.size === 0) return prev;
      return new Set([...prev].filter((n) => names.includes(n)));
    });
  };

  const loadPresets = async () => {
    setBusy("preset");
    setError(null);
    try {
      applySchema(await api.suggestMysqlMappings(slug, { source: "preset" }));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  useEffect(() => {
    if (!autofetch) return;
    let cancelled = false;
    (async () => {
      setBusy("preset");
      setError(null);
      try {
        const res = await api.suggestMysqlMappings(slug, { source: "preset" });
        if (cancelled) return;
        applySchema(res);
      } catch (e) {
        if (!cancelled) setError(e instanceof ApiError ? e.message : String(e));
      } finally {
        if (!cancelled) setBusy(null);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, autofetch]);

  const toggle = (name: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const applyAi = async () => {
    if (tableNames.length > 0 && selected.size === 0) {
      setError("Tick the tables the model should read.");
      return;
    }
    setBusy("ai");
    setError(null);
    try {
      const tables = selected.size > 0 ? [...selected] : undefined;
      const res = await api.suggestMysqlMappings(slug, { source: "ai", tables });
      if (res.suggestions.length === 0) {
        setError("The model could not map this schema — pick a template or add a table by hand.");
        return;
      }
      const unresolved = res.schema_summary.unresolved_money_tables ?? [];
      setLastUnresolved(unresolved);
      onApply(res.suggestions, unresolved);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  if (!autofetch && !open) {
    return (
      <div style={{ marginTop: 10 }}>
        <button type="button" className="btn-text" onClick={() => { setOpen(true); if (!presets) void loadPresets(); }}>
          Suggest mappings…
        </button>
      </div>
    );
  }

  const picked = selected.size;

  return (
    <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
        <span className="eyebrow">Suggest mappings</span>
        {tableNames.length > 0 && (
          <span style={{ fontSize: 12, color: "var(--text-faint)" }}>
            {tableNames.length} table{tableNames.length === 1 ? "" : "s"}
            {filter.trim() ? ` · ${visible.length} shown` : ""}
            {" · "}{picked} selected
          </span>
        )}
      </div>

      {busy === "preset" && presets === null && (
        <div style={{ fontSize: 12.5, color: "var(--text-faint)" }}>Reading your schema…</div>
      )}

      {tableNames.length > 0 && (
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6, flexWrap: "wrap" }}>
            <input className="input" style={{ minHeight: 34, fontSize: 12.5, flex: "1 1 180px", maxWidth: 280 }}
                   placeholder="Filter tables…" value={filter} aria-label="Filter tables"
                   onChange={(e) => setFilter(e.target.value)} />
            <span style={{ fontSize: 12.5, color: "var(--text-3)" }}>
              Tick tables to send to AI (max 30).
            </span>
            <span style={{ flex: 1 }} />
            <button type="button" className="btn-text" disabled={busy !== null || visible.length === 0}
                    onClick={() => setSelected((prev) => {
                      const next = new Set(prev);
                      for (const n of visible) {
                        if (next.size >= 30) break;
                        next.add(n);
                      }
                      return next;
                    })}>
              Select visible
            </button>
            <button type="button" className="btn-text" disabled={busy !== null || picked === 0}
                    onClick={() => setSelected(new Set())}>
              Clear
            </button>
          </div>
          <div style={{
            maxHeight: 320, overflow: "auto",
            border: "1px solid var(--border-hairline)", borderRadius: 10,
            padding: "8px 12px",
            display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: "2px 12px",
          }}>
            {visible.length === 0 && (
              <span style={{ fontSize: 12.5, color: "var(--text-faint)", gridColumn: "1 / -1" }}>
                No tables match “{filter.trim()}”.
              </span>
            )}
            {visible.map((name) => (
              <label key={name} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5,
                                          color: "var(--text-2)", minHeight: 28 }}>
                <input type="checkbox" checked={selected.has(name)}
                       disabled={busy !== null || (!selected.has(name) && picked >= 30)}
                       onChange={() => toggle(name)} />
                <span className="num" style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{name}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {presets && presets.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {presets.map((p) => (
            <div key={p.id} style={{ border: "1px solid var(--border-hairline)", borderRadius: 10, padding: "10px 12px",
                                     display: "flex", flexDirection: "column", gap: 4 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontWeight: 600 }}>{p.label}</span>
                <span style={{ flex: 1 }} />
                <button type="button" className="btn-text" disabled={busy !== null}
                        onClick={() => onApply(p.event_maps, lastUnresolved)}>
                  Use this
                </button>
              </div>
              <span style={{ fontSize: 12.5, color: "var(--text-3)" }}>{p.description}</span>
              <details style={{ fontSize: 12, color: "var(--text-faint)" }}>
                <summary>{summarizeMaps(p.event_maps)}</summary>
              </details>
            </div>
          ))}
        </div>
      )}

      {presets && presets.length === 0 && (
        <div style={{ fontSize: 12.5, color: "var(--text-faint)" }}>
          No common template fits this schema — tick tables and try AI.
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <button type="button" className="btn-solid" style={{ minHeight: 36 }}
                disabled={busy !== null || (tableNames.length > 0 && picked === 0)}
                onClick={() => void applyAi()}>
          {busy === "ai" ? "Asking the model…" : "AI suggest"}
        </button>
        <span style={{ fontSize: 12, color: "var(--text-faint)", lineHeight: 1.45, maxWidth: 420 }}>
          Sample rows from the ticked tables are sent to our AI provider (OpenRouter).
        </span>
      </div>
      {error && <div className="form-error">{error}</div>}
    </div>
  );
}

// Shared Meta ad accounts book every campaign onto the product unless a name
// prefix filter is set. Empty = ingest all (the dedicated-account default).
function MetaCampaignFilterEditor({ c, reload }: { c: Connection; reload: () => void }) {
  const initial = Array.isArray(c.config?.campaign_name_prefixes)
    ? (c.config!.campaign_name_prefixes as string[]).join(", ")
    : "";
  const [value, setValue] = useState(initial);
  const [open, setOpen] = useState(initial.length > 0);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setNote(null);
    try {
      const prefixes = value.split(",").map((p) => p.trim()).filter(Boolean);
      const res = await api.setMetaAdsCampaignFilter(c.product_slug, prefixes);
      setValue(res.campaign_name_prefixes.join(", "));
      reload();
      followSync(reload);
      setNote(res.campaign_name_prefixes.length
        ? `Keeping campaigns starting with ${res.campaign_name_prefixes.join(", ")}. A sync is running; non-matching spend will be dropped.`
        : "Filter cleared. A sync is running; every campaign in the ad account will be ingested.");
    } catch (e) {
      setNote(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--border-hairline)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span className="eyebrow">Campaign filter</span>
        <span style={{ fontSize: 12.5, color: "var(--text-faint)" }}>
          {initial ? `keeping ${initial}` : "all campaigns"}
        </span>
        <span style={{ flex: 1 }} />
        <button type="button" className="btn-text" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
          {open ? "Hide" : "Edit"}
        </button>
      </div>
      {open && (
        <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
          <input
            className="input"
            style={{ minHeight: 36, fontSize: 13 }}
            value={value}
            placeholder="META_, FK_  — comma-separated prefixes"
            maxLength={400}
            aria-label="Campaign name prefixes"
            onChange={(e) => setValue(e.target.value)}
          />
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span style={{ fontSize: 12, color: "var(--text-faint)", lineHeight: 1.55, flex: 1 }}>
              Only campaigns whose names start with one of these prefixes are ingested.
              Leave blank to keep every campaign. Use this when the ad account is shared.
            </span>
            <button type="button" className="btn-ghost" style={{ minHeight: 36 }} onClick={() => void save()} disabled={busy}>
              {busy ? "Saving…" : "Save filter"}
            </button>
          </div>
          {note && <div style={{ fontSize: 12.5, color: "var(--text-3)" }}>{note}</div>}
        </div>
      )}
    </div>
  );
}

// Postgres credentials: same shape as MySQL (host/port/user/pass/database)
// plus an optional default schema. Tenant-only — no env-ref path.
// GA4 event → stage mapping. Data, not code: the daily sync ingests each
// mapped event as aggregate stage rows, so funnel steps (and ga4-bound
// conversions) can be built from GA4 events. One source per stage — a stage
// fed by both GA4 and MySQL double-counts.
function Ga4EventMapEditor({ c, reload }: { c: Connection; reload: () => void }) {
  const initial = (Array.isArray(c.config?.event_maps) ? c.config!.event_maps : []) as Ga4EventMap[];
  const [maps, setMaps] = useState<Ga4EventMap[]>(() => initial.map((m) => ({ ...m })));
  const [open, setOpen] = useState(initial.length > 0);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setNote(null);
    try {
      const clean = maps.filter((m) => m.event.trim());
      await api.setGa4EventMaps(c.product_slug, clean.map((m) => ({ event: m.event.trim(), stage: m.stage })));
      setMaps(clean);
      reload();
      followSync(reload);
      setNote("Saved. A sync is running now; after this one the daily job ingests mapped events as funnel-stage rows.");
    } catch (e) {
      setNote(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--border-hairline)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span className="eyebrow">Event mapping</span>
        <span style={{ fontSize: 12, color: "var(--text-faint)" }}>
          {maps.length === 0 ? "none yet" : `${maps.length} event${maps.length === 1 ? "" : "s"} → stages`}
        </span>
        <span style={{ flex: 1 }} />
        <button type="button" className="btn-text" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
          {open ? "Hide" : "Edit"}
        </button>
      </div>

      {open && (
        <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
          {maps.map((m, i) => (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) 32px", gap: 10, alignItems: "center" }}>
              <input className="input" style={{ minHeight: 36, fontSize: 13 }} value={m.event}
                     placeholder="generate_lead" maxLength={120} aria-label={`GA4 event ${i + 1}`}
                     onChange={(e) => setMaps(maps.map((x, j) => (j === i ? { ...x, event: e.target.value } : x)))} />
              <select className="input" style={{ minHeight: 36, fontSize: 13 }} value={m.stage}
                      aria-label={`Stage for event ${i + 1}`}
                      onChange={(e) => setMaps(maps.map((x, j) => (j === i ? { ...x, stage: e.target.value as Stage } : x)))}>
                {STAGES.map((st) => <option key={st} value={st}>{STAGE_LABELS[st]} ({st})</option>)}
              </select>
              <button type="button" className="wtool wtool-x" title="Remove mapping"
                      onClick={() => setMaps(maps.filter((_, j) => j !== i))}>×</button>
            </div>
          ))}
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <button type="button" className="btn-text" disabled={maps.length >= 20}
                    onClick={() => setMaps([...maps, { event: "", stage: "lead" }])}>
              + Add an event
            </button>
            <span style={{ flex: 1 }} />
            <button type="button" className="btn-ghost" style={{ minHeight: 36 }} onClick={save} disabled={busy}>
              {busy ? "Saving…" : "Save mapping"}
            </button>
          </div>
          <div style={{ fontSize: 12, color: "var(--text-faint)", lineHeight: 1.55 }}>
            Use the event names exactly as GA4 reports them (e.g. <code>generate_lead</code>,{" "}
            <code>form_submit</code>). Map each stage from one source only — a stage fed by both
            GA4 and MySQL counts twice.
          </div>
          {note && <div style={{ fontSize: 12.5, color: "var(--text-3)" }}>{note}</div>}
        </div>
      )}
    </div>
  );
}

// PostHog event → stage mapping. Same contract as GA4's: mapped events are
// ingested as daily aggregate stage rows, and a stage must be fed from one
// source only. Traffic is separate — PostHog yields visits only when GA4 is
// not connected, which the card's Next step spells out.
function PosthogEventMapEditor({ c, reload }: { c: Connection; reload: () => void }) {
  const initial = (Array.isArray(c.config?.event_maps) ? c.config!.event_maps : []) as PosthogEventMap[];
  const [maps, setMaps] = useState<PosthogEventMap[]>(() => initial.map((m) => ({ ...m })));
  const [open, setOpen] = useState(initial.length > 0);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setNote(null);
    try {
      const clean = maps.filter((m) => m.event.trim());
      await api.setPosthogEventMaps(c.product_slug, clean.map((m) => ({ event: m.event.trim(), stage: m.stage })));
      setMaps(clean);
      reload();
      followSync(reload);
      setNote("Saved. A sync is running now; after this one the daily job ingests mapped events as funnel-stage rows.");
    } catch (e) {
      setNote(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--border-hairline)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span className="eyebrow">Event mapping</span>
        <span style={{ fontSize: 12, color: "var(--text-faint)" }}>
          {maps.length === 0 ? "none yet" : `${maps.length} event${maps.length === 1 ? "" : "s"} → stages`}
        </span>
        <span style={{ flex: 1 }} />
        <button type="button" className="btn-text" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
          {open ? "Hide" : "Edit"}
        </button>
      </div>

      {open && (
        <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
          {maps.map((m, i) => (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) 32px", gap: 10, alignItems: "center" }}>
              <input className="input" style={{ minHeight: 36, fontSize: 13 }} value={m.event}
                     placeholder="signed_up" maxLength={120} aria-label={`PostHog event ${i + 1}`}
                     onChange={(e) => setMaps(maps.map((x, j) => (j === i ? { ...x, event: e.target.value } : x)))} />
              <select className="input" style={{ minHeight: 36, fontSize: 13 }} value={m.stage}
                      aria-label={`Stage for event ${i + 1}`}
                      onChange={(e) => setMaps(maps.map((x, j) => (j === i ? { ...x, stage: e.target.value as Stage } : x)))}>
                {STAGES.map((st) => <option key={st} value={st}>{STAGE_LABELS[st]} ({st})</option>)}
              </select>
              <button type="button" className="wtool wtool-x" title="Remove mapping"
                      onClick={() => setMaps(maps.filter((_, j) => j !== i))}>×</button>
            </div>
          ))}
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <button type="button" className="btn-text" disabled={maps.length >= 20}
                    onClick={() => setMaps([...maps, { event: "", stage: "lead" }])}>
              + Add an event
            </button>
            <span style={{ flex: 1 }} />
            <button type="button" className="btn-ghost" style={{ minHeight: 36 }} onClick={save} disabled={busy}>
              {busy ? "Saving…" : "Save mapping"}
            </button>
          </div>
          <div style={{ fontSize: 12, color: "var(--text-faint)", lineHeight: 1.55 }}>
            Use the event names exactly as PostHog reports them (e.g. <code>signed_up</code>,{" "}
            <code>$pageview</code>). Map each stage from one source only — a stage fed by both
            PostHog and GA4 counts twice.
          </div>
          {note && <div style={{ fontSize: 12.5, color: "var(--text-3)" }}>{note}</div>}
        </div>
      )}
    </div>
  );
}

// MongoDB event maps: the collection→stage mapping, editable in place. Field
// paths may use dot notation to reach nested values. Each map may stitch an
// identity, resolve a channel (attribution field paths), and carry a revenue
// field path whose per-document amounts back 'transaction'-mode conversions.
function MongoEventMapEditor({ c, reload }: { c: Connection; reload: () => void }) {
  const initial = (Array.isArray(c.config?.event_maps) ? c.config!.event_maps : []) as MongoEventMap[];
  const [maps, setMaps] = useState<MongoEventMap[]>(() => initial.map((m) => structuredClone(m)));
  const [open, setOpen] = useState(false);
  const [attrOpen, setAttrOpen] = useState<Record<number, boolean>>({});
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const patch = (i: number, fn: (m: MongoEventMap) => MongoEventMap) =>
    setMaps(maps.map((m, j) => (j === i ? fn(m) : m)));
  const clean = (m: MongoEventMap): MongoEventMap => {
    const trimmed = <T extends Record<string, string | undefined>>(o?: T) => {
      if (!o) return undefined;
      const out: Record<string, string> = {};
      for (const [k, v] of Object.entries(o)) if (v && v.trim()) out[k] = v.trim();
      return Object.keys(out).length > 0 ? (out as T) : undefined;
    };
    return {
      collection: m.collection.trim(), pk_field: m.pk_field.trim(), ts_field: m.ts_field.trim(), stage: m.stage,
      score_field: m.score_field?.trim() || undefined,
      identity: trimmed(m.identity),
      attribution: trimmed(m.attribution),
      revenue: persistRevenue("amount_field", {
        amount: m.revenue?.amount_field ?? "",
        currency: m.revenue?.currency || "USD",
        is_dollars: m.revenue?.is_dollars,
        basis: m.revenue?.basis,
      }) as MongoEventMap["revenue"],
    };
  };

  const save = async () => {
    setBusy(true);
    setNote(null);
    try {
      const body = maps.filter((m) => m.collection.trim()).map(clean);
      await api.setMongoEventMaps(c.product_slug, body);
      setMaps(body);
      reload();
      followSync(reload);
      setNote(REMAP_NOTE);
    } catch (e) {
      setNote(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const smallInput = (value: string | undefined, placeholder: string, onChange: (v: string) => void, label: string) => (
    <input className="input" style={{ minHeight: 34, fontSize: 12.5 }} value={value ?? ""}
           placeholder={placeholder} aria-label={label} onChange={(e) => onChange(e.target.value)} />
  );

  return (
    <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--border-hairline)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span className="eyebrow">Event maps</span>
        <span style={{ fontSize: 12, color: "var(--text-faint)" }}>
          {maps.length === 0 ? "none yet — nothing syncs without them" : `${maps.length} collection${maps.length === 1 ? "" : "s"} → stages`}
        </span>
        <span style={{ flex: 1 }} />
        <button type="button" className="btn-text" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
          {open ? "Hide" : "Edit"}
        </button>
      </div>

      {open && (
        <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 12 }}>
          {maps.map((m, i) => (
            <div key={i} style={{ border: "1px solid var(--border-hairline)", borderRadius: 10, padding: "12px 14px",
                                  display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                            gap: 8, alignItems: "center" }}>
                {smallInput(m.collection, "collection", (v) => patch(i, (x) => ({ ...x, collection: v })), `Collection for map ${i + 1}`)}
                {smallInput(m.pk_field, "pk field (_id)", (v) => patch(i, (x) => ({ ...x, pk_field: v })), `Primary key field for map ${i + 1}`)}
                {smallInput(m.ts_field, "timestamp field", (v) => patch(i, (x) => ({ ...x, ts_field: v })), `Timestamp field for map ${i + 1}`)}
                <select className="input" style={{ minHeight: 34, fontSize: 12.5 }} value={m.stage}
                        aria-label={`Stage for map ${i + 1}`}
                        onChange={(e) => patch(i, (x) => ({ ...x, stage: e.target.value as Stage }))}>
                  {STAGES.map((st) => <option key={st} value={st}>{STAGE_LABELS[st]} ({st})</option>)}
                </select>
                <button type="button" className="wtool wtool-x" title="Remove map"
                        onClick={() => setMaps(maps.filter((_, j) => j !== i))}>×</button>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8 }}>
                {smallInput(m.identity?.email_field, "email field (identity)",
                  (v) => patch(i, (x) => ({ ...x, identity: { ...x.identity, email_field: v } })), `Email field for map ${i + 1}`)}
                {smallInput(m.identity?.external_id_field, "person/listing id field",
                  (v) => patch(i, (x) => ({ ...x, identity: { ...x.identity, external_id_field: v } })), `External id field for map ${i + 1}`)}
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "var(--text-3)" }}>
                    <input type="checkbox" checked={Boolean(m.revenue)}
                           onChange={(e) => patch(i, (x) => ({
                             ...x, revenue: e.target.checked ? { amount_field: "", currency: "USD", is_dollars: true } : undefined,
                           }))} />
                    Revenue on this collection — your fee, not the sale price
                  </label>
                  <span style={{ flex: 1 }} />
                  <button type="button" className="btn-text" aria-expanded={Boolean(attrOpen[i])}
                          onClick={() => setAttrOpen({ ...attrOpen, [i]: !attrOpen[i] })}>
                    {attrOpen[i] ? "Hide attribution fields" : "Attribution fields…"}
                  </button>
                </div>
                {m.revenue && (
                  <RevenueAmountField
                    table=""
                    fieldLabel={`Revenue field for map ${i + 1}`}
                    money={[]}
                    value={{
                      amount: m.revenue.amount_field,
                      currency: m.revenue.currency,
                      is_dollars: m.revenue.is_dollars,
                      basis: m.revenue.basis,
                    }}
                    onChange={(rev) => patch(i, (x) => ({
                      ...x,
                      revenue: {
                        amount_field: rev.amount,
                        currency: rev.currency,
                        is_dollars: rev.is_dollars,
                        basis: rev.basis,
                      },
                    }))}
                  />
                )}
              </div>

              {attrOpen[i] && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 8 }}>
                  {([
                    ["utm_source_field", "utm_source field"], ["utm_medium_field", "utm_medium field"],
                    ["utm_campaign_field", "utm_campaign field"], ["gclid_field", "gclid field"],
                    ["fbclid_field", "fbclid field"], ["li_fat_id_field", "li_fat_id field"], ["referrer_field", "referrer field"],
                    ["channel_field", "channel field (canonical)"],
                  ] as const).map(([key, ph]) => (
                    <span key={key}>
                      {smallInput(m.attribution?.[key], ph,
                        (v) => patch(i, (x) => ({ ...x, attribution: { ...x.attribution, [key]: v } })), `${ph} for map ${i + 1}`)}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}

          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <button type="button" className="btn-text" disabled={maps.length >= 20}
                    onClick={() => setMaps([...maps, { collection: "", pk_field: "_id", ts_field: "createdAt", stage: "lead" }])}>
              + Add a collection
            </button>
            <span style={{ flex: 1 }} />
            <button type="button" className="btn-ghost" style={{ minHeight: 36 }} onClick={save} disabled={busy}>
              {busy ? "Saving…" : "Save event maps"}
            </button>
          </div>
          <div style={{ fontSize: 12, color: "var(--text-faint)", lineHeight: 1.55 }}>
            One stage per source (a stage fed twice double-counts), one revenue map per collection, and put
            the shared person/listing id on every map so the stages stitch to one identity. Field paths may
            use dot notation (<code>user.email</code>) to reach nested values.
          </div>
          {note && <div style={{ fontSize: 12.5, color: "var(--text-3)" }}>{note}</div>}
        </div>
      )}
    </div>
  );
}

// Postgres event maps: the table→stage mapping, editable in place. Same shape
// as MySQL, plus an optional per-map schema. Each map may stitch an identity,
// resolve a channel (attribution columns), and carry a revenue column whose
// per-row amounts back 'transaction'-mode conversions.
function PostgresEventMapEditor({ c, reload }: { c: Connection; reload: () => void }) {
  const initial = (Array.isArray(c.config?.event_maps) ? c.config!.event_maps : []) as PostgresEventMap[];
  const [maps, setMaps] = useState<PostgresEventMap[]>(() => initial.map((m) => structuredClone(m)));
  const [open, setOpen] = useState(false);
  const [attrOpen, setAttrOpen] = useState<Record<number, boolean>>({});
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const patch = (i: number, fn: (m: PostgresEventMap) => PostgresEventMap) =>
    setMaps(maps.map((m, j) => (j === i ? fn(m) : m)));
  const clean = (m: PostgresEventMap): PostgresEventMap => {
    const trimmed = <T extends Record<string, string | undefined>>(o?: T) => {
      if (!o) return undefined;
      const out: Record<string, string> = {};
      for (const [k, v] of Object.entries(o)) if (v && v.trim()) out[k] = v.trim();
      return Object.keys(out).length > 0 ? (out as T) : undefined;
    };
    return {
      ...(m.schema?.trim() ? { schema: m.schema.trim() } : {}),
      table: m.table.trim(), pk: m.pk.trim(), ts_col: m.ts_col.trim(), stage: m.stage,
      score_col: m.score_col?.trim() || undefined,
      identity: trimmed(m.identity),
      attribution: trimmed(m.attribution),
      revenue: persistRevenue("amount_col", {
        amount: m.revenue?.amount_col ?? "",
        currency: m.revenue?.currency || "USD",
        is_dollars: m.revenue?.is_dollars,
        basis: m.revenue?.basis,
      }) as PostgresEventMap["revenue"],
    };
  };

  const save = async () => {
    setBusy(true);
    setNote(null);
    try {
      const body = maps.filter((m) => m.table.trim()).map(clean);
      await api.setPostgresEventMaps(c.product_slug, body);
      setMaps(body);
      reload();
      followSync(reload);
      setNote(REMAP_NOTE);
    } catch (e) {
      setNote(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const smallInput = (value: string | undefined, placeholder: string, onChange: (v: string) => void, label: string) => (
    <input className="input" style={{ minHeight: 34, fontSize: 12.5 }} value={value ?? ""}
           placeholder={placeholder} aria-label={label} onChange={(e) => onChange(e.target.value)} />
  );

  return (
    <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--border-hairline)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span className="eyebrow">Event maps</span>
        <span style={{ fontSize: 12, color: "var(--text-faint)" }}>
          {maps.length === 0 ? "none yet — nothing syncs without them" : `${maps.length} table${maps.length === 1 ? "" : "s"} → stages`}
        </span>
        <span style={{ flex: 1 }} />
        <button type="button" className="btn-text" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
          {open ? "Hide" : "Edit"}
        </button>
      </div>

      {open && (
        <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 12 }}>
          {maps.map((m, i) => (
            <div key={i} style={{ border: "1px solid var(--border-hairline)", borderRadius: 10, padding: "12px 14px",
                                  display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                            gap: 8, alignItems: "center" }}>
                {smallInput(m.schema, "schema (opt.)", (v) => patch(i, (x) => ({ ...x, schema: v })), `Schema for map ${i + 1}`)}
                {smallInput(m.table, "table", (v) => patch(i, (x) => ({ ...x, table: v })), `Table for map ${i + 1}`)}
                {smallInput(m.pk, "pk (id)", (v) => patch(i, (x) => ({ ...x, pk: v })), `Primary key for map ${i + 1}`)}
                {smallInput(m.ts_col, "timestamp col", (v) => patch(i, (x) => ({ ...x, ts_col: v })), `Timestamp column for map ${i + 1}`)}
                <select className="input" style={{ minHeight: 34, fontSize: 12.5 }} value={m.stage}
                        aria-label={`Stage for map ${i + 1}`}
                        onChange={(e) => patch(i, (x) => ({ ...x, stage: e.target.value as Stage }))}>
                  {STAGES.map((st) => <option key={st} value={st}>{STAGE_LABELS[st]} ({st})</option>)}
                </select>
                <button type="button" className="wtool wtool-x" title="Remove map"
                        onClick={() => setMaps(maps.filter((_, j) => j !== i))}>×</button>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8 }}>
                {smallInput(m.identity?.email_col, "email column (identity)",
                  (v) => patch(i, (x) => ({ ...x, identity: { ...x.identity, email_col: v } })), `Email column for map ${i + 1}`)}
                {smallInput(m.identity?.external_id_col, "person/listing id column",
                  (v) => patch(i, (x) => ({ ...x, identity: { ...x.identity, external_id_col: v } })), `External id column for map ${i + 1}`)}
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "var(--text-3)" }}>
                    <input type="checkbox" checked={Boolean(m.revenue)}
                           onChange={(e) => patch(i, (x) => ({
                             ...x, revenue: e.target.checked ? { amount_col: "", currency: "USD", is_dollars: true } : undefined,
                           }))} />
                    Revenue on this table — your fee, not the sale price
                  </label>
                  <span style={{ flex: 1 }} />
                  <button type="button" className="btn-text" aria-expanded={Boolean(attrOpen[i])}
                          onClick={() => setAttrOpen({ ...attrOpen, [i]: !attrOpen[i] })}>
                    {attrOpen[i] ? "Hide attribution columns" : "Attribution columns…"}
                  </button>
                </div>
                {m.revenue && (
                  <RevenueAmountField
                    table={m.table}
                    fieldLabel={`Revenue column for map ${i + 1}`}
                    money={[]}
                    value={{
                      amount: m.revenue.amount_col,
                      currency: m.revenue.currency,
                      is_dollars: m.revenue.is_dollars,
                      basis: m.revenue.basis,
                    }}
                    onChange={(rev) => patch(i, (x) => ({
                      ...x,
                      revenue: {
                        amount_col: rev.amount,
                        currency: rev.currency,
                        is_dollars: rev.is_dollars,
                        basis: rev.basis,
                      },
                    }))}
                  />
                )}
              </div>

              {attrOpen[i] && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 8 }}>
                  {([
                    ["utm_source_col", "utm_source col"], ["utm_medium_col", "utm_medium col"],
                    ["utm_campaign_col", "utm_campaign col"], ["gclid_col", "gclid col"],
                    ["fbclid_col", "fbclid col"], ["li_fat_id_col", "li_fat_id col"], ["referrer_col", "referrer col"],
                    ["channel_col", "channel col (canonical)"],
                  ] as const).map(([key, ph]) => (
                    <span key={key}>
                      {smallInput(m.attribution?.[key], ph,
                        (v) => patch(i, (x) => ({ ...x, attribution: { ...x.attribution, [key]: v } })), `${ph} for map ${i + 1}`)}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}

          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <button type="button" className="btn-text" disabled={maps.length >= 20}
                    onClick={() => setMaps([...maps, { table: "", pk: "id", ts_col: "created_at", stage: "lead" }])}>
              + Add a table
            </button>
            <span style={{ flex: 1 }} />
            <button type="button" className="btn-ghost" style={{ minHeight: 36 }} onClick={save} disabled={busy}>
              {busy ? "Saving…" : "Save event maps"}
            </button>
          </div>
          <div style={{ fontSize: 12, color: "var(--text-faint)", lineHeight: 1.55 }}>
            One stage per source (a stage fed twice double-counts), one revenue map per table, and put
            the shared person/listing id on every map so the stages stitch to one identity. Set the
            schema to reach a non-public namespace.
          </div>
          {note && <div style={{ fontSize: 12.5, color: "var(--text-3)" }}>{note}</div>}
        </div>
      )}
    </div>
  );
}

function ConnectionActions({ c, reload }: { c: Connection; reload: () => void }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [showKeyForm, setShowKeyForm] = useState(false);
  const [showClarityForm, setShowClarityForm] = useState(false);
  const [showPosthogForm, setShowPosthogForm] = useState(false);
  const [showOpinlyForm, setShowOpinlyForm] = useState(false);
  const [showMysqlForm, setShowMysqlForm] = useState(false);
  const [showPostgresForm, setShowPostgresForm] = useState(false);
  const [showMongoForm, setShowMongoForm] = useState(false);
  const [key, setKey] = useState("");
  const [domain, setDomain] = useState((c.config?.domain as string) ?? "");
  const [clarityToken, setClarityToken] = useState("");
  const [clarityProject, setClarityProject] = useState((c.config?.project_id as string) ?? "");
  const [posthogKey, setPosthogKey] = useState("");
  const [posthogProject, setPosthogProject] = useState((c.config?.project_id as string) ?? "");
  const [posthogHost, setPosthogHost] = useState((c.config?.host as string) ?? "https://us.posthog.com");
  const [opinlyKey, setOpinlyKey] = useState("");
  const [opinlyCompany, setOpinlyCompany] = useState((c.config?.company_id as string) ?? "");
  const [showBingForm, setShowBingForm] = useState(false);
  const [bingKey, setBingKey] = useState("");
  const [bingSite, setBingSite] = useState((c.config?.site_url as string) ?? "");
  const [showAhrefsForm, setShowAhrefsForm] = useState(false);
  const [ahrefsKey, setAhrefsKey] = useState("");
  const [ahrefsDomain, setAhrefsDomain] = useState((c.config?.domain as string) ?? "");
  const [showPaidSocialForm, setShowPaidSocialForm] = useState(false);
  const [showStripeForm, setShowStripeForm] = useState(false);

  const googleKind = googleOAuthKind(c.kind);

  const run = async (label: string, fn: () => Promise<string>) => {
    setBusy(label);
    setNote(null);
    try {
      setNote(await fn());
    } catch (e) {
      setNote(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="integration-actions">
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {googleKind && (
          <button type="button" className="btn-ghost" disabled={busy !== null}
            onClick={() => run("connect", async () => {
              const start = await api.connect.start(c.product_slug, [c.kind]);
              location.href = start.auth_url; // leaves for Google; returns to #/connected/<state>
              return "Redirecting to Google…";
            })}>
            {c.status === "active" ? "Reconnect" : "Connect"}
          </button>
        )}
        {c.kind === "semrush" && (
          <button type="button" className="btn-ghost" disabled={busy !== null}
            onClick={() => setShowKeyForm((v) => !v)}>
            {c.credential_id ? "Replace key" : "Set key"}
          </button>
        )}
        {c.kind === "clarity" && (
          <button type="button" className="btn-ghost" disabled={busy !== null}
            onClick={() => setShowClarityForm((v) => !v)}>
            {c.credential_id ? "Replace token" : "Set token"}
          </button>
        )}
        {c.kind === "posthog" && (
          <button type="button" className="btn-ghost" disabled={busy !== null}
            onClick={() => setShowPosthogForm((v) => !v)}>
            {c.credential_id ? "Replace key" : "Set key"}
          </button>
        )}
        {c.kind === "opinly" && (
          <button type="button" className="btn-ghost" disabled={busy !== null}
            onClick={() => setShowOpinlyForm((v) => !v)}>
            {c.credential_id ? "Replace key" : "Set key"}
          </button>
        )}
        {c.kind === "bing" && (
          <button type="button" className="btn-ghost" disabled={busy !== null}
            onClick={() => setShowBingForm((v) => !v)}>
            {c.credential_id ? "Replace key" : "Set key"}
          </button>
        )}
        {c.kind === "ahrefs" && (
          <button type="button" className="btn-ghost" disabled={busy !== null}
            onClick={() => setShowAhrefsForm((v) => !v)}>
            {c.credential_id ? "Replace token" : "Set token"}
          </button>
        )}
        {(c.kind === "meta_ads" || c.kind === "linkedin_ads" || c.kind === "meta_social" || c.kind === "x_social") && (
          <button type="button" className="btn-ghost" disabled={busy !== null}
            onClick={() => setShowPaidSocialForm((v) => !v)}>
            {c.credential_id ? "Replace token" : "Set token"}
          </button>
        )}
        {c.kind === "stripe" && (
          <button type="button" className="btn-ghost" disabled={busy !== null}
            onClick={() => setShowStripeForm((v) => !v)}>
            {c.credential_id ? "Replace key" : "Set key"}
          </button>
        )}
        {c.kind === "mysql" && (
          <button type="button" className="btn-ghost" disabled={busy !== null}
            onClick={() => setShowMysqlForm((v) => !v)}>
            {c.credential_id ? "Replace credentials" : "Set credentials"}
          </button>
        )}
        {c.kind === "postgres" && (
          <button type="button" className="btn-ghost" disabled={busy !== null}
            onClick={() => setShowPostgresForm((v) => !v)}>
            {c.credential_id ? "Replace credentials" : "Set credentials"}
          </button>
        )}
        {c.kind === "mongo" && (
          <button type="button" className="btn-ghost" disabled={busy !== null}
            onClick={() => setShowMongoForm((v) => !v)}>
            {c.credential_id ? "Replace connection string" : "Set connection string"}
          </button>
        )}
        <button type="button" className="btn-ghost" disabled={busy !== null}
          onClick={() => run("test", async () => {
            const r = await api.testConnection(c.product_slug, c.kind);
            // The card's status and error text are stale the moment a test
            // changes them — refetch so the badge and the note agree.
            reload();
            return `${r.ok ? "OK" : "Failed"} — ${r.detail}`;
          })}>
          {busy === "test" ? "Testing…" : "Test"}
        </button>
        {SYNCABLE_KINDS.includes(c.kind) && (
          <button type="button" className="btn-ghost" disabled={busy !== null || c.syncing}
            onClick={() => run("sync", async () => {
              const r = await api.syncConnection(c.product_slug, c.kind);
              reload();
              if (r.started) followSync(reload);
              return r.detail;
            })}>
            {c.syncing ? "Syncing…" : busy === "sync" ? "Starting…" : "Sync now"}
          </button>
        )}
        <button type="button" className="btn-ghost" disabled={busy !== null}
          onClick={() => {
            if (!window.confirm(
              `Disconnect ${kindMeta(c.kind).label}? This deletes the stored credentials and stops `
              + "collection from this source. Data already collected stays.",
            )) return;
            void run("disconnect", async () => {
              const r = await api.disconnectConnection(c.product_slug, c.kind);
              reload();
              if (!r.credential_deleted) {
                return "Disconnected. The credential stays because another Google source still uses it.";
              }
              return r.revoked_upstream
                ? "Disconnected. Credentials deleted, and the grant revoked with Google."
                : "Disconnected. Credentials deleted.";
            });
          }}>
          {busy === "disconnect" ? "Disconnecting…" : "Disconnect"}
        </button>
      </div>

      {googleSaKind(c.kind) && (
        <GrantAccessInstead
          slug={c.product_slug}
          kinds={[c.kind]}
          onDone={(msg) => { setNote(msg); reload(); }}
        />
      )}

      {showKeyForm && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, maxWidth: 420 }}>
          <input className="input" type="password" placeholder="SEMrush API key"
            value={key} onChange={(e) => setKey(e.target.value)} />
          <input className="input" placeholder="Domain to track (example.com)"
            value={domain} onChange={(e) => setDomain(e.target.value)} />
          <div>
            <button type="button" className="btn-solid" disabled={!key || !domain || busy !== null}
              onClick={() => run("savekey", async () => {
                await api.setSemrushKey(c.product_slug, key.trim(), domain.trim());
                setShowKeyForm(false);
                setKey("");
                reload();
                return "Key stored (encrypted). The weekly snapshot is the live test.";
              })}>
              Save key
            </button>
          </div>
        </div>
      )}

      {showClarityForm && (
          <ClarityTokenForm
          projectId={clarityProject}
          token={clarityToken}
          busy={busy !== null}
          onProjectId={setClarityProject}
          onToken={setClarityToken}
          onSave={() => run("saveclarity", async () => {
            await api.connectClarity(c.product_slug, clarityToken.trim(), clarityProject.trim());
            setShowClarityForm(false);
            setClarityToken("");
            reload();
            return "Token stored (encrypted). The daily job samples recordings.";
          })}
        />
      )}

      {showPosthogForm && (
        <PosthogKeyForm
          projectId={posthogProject}
          apiKey={posthogKey}
          host={posthogHost}
          busy={busy !== null}
          onProjectId={setPosthogProject}
          onApiKey={setPosthogKey}
          onHost={setPosthogHost}
          onSave={() => run("saveposthog", async () => {
            await api.connectPosthog(
              c.product_slug, posthogKey.trim(), posthogProject.trim(), posthogHost.trim() || undefined);
            setShowPosthogForm(false);
            setPosthogKey("");
            reload();
            followSync(reload);
            return "Key stored (encrypted). A sync is running now — map events onto stages below.";
          })}
        />
      )}

      {showOpinlyForm && (
        <OpinlyKeyForm
          apiKey={opinlyKey}
          companyId={opinlyCompany}
          busy={busy !== null}
          onApiKey={setOpinlyKey}
          onCompanyId={setOpinlyCompany}
          onSave={() => run("saveopinly", async () => {
            await api.connectOpinly(c.product_slug, opinlyKey.trim(), opinlyCompany.trim() || undefined);
            setShowOpinlyForm(false);
            setOpinlyKey("");
            reload();
            followSync(reload);
            return "Key stored (encrypted). A snapshot is running now.";
          })}
        />
      )}

      {showBingForm && (
        <BingKeyForm
          apiKey={bingKey}
          siteUrl={bingSite}
          busy={busy !== null}
          onApiKey={setBingKey}
          onSiteUrl={setBingSite}
          onSave={() => run("savebing", async () => {
            await api.connectBing(c.product_slug, bingKey.trim(), bingSite.trim());
            setShowBingForm(false);
            setBingKey("");
            reload();
            followSync(reload);
            return "Key stored (encrypted). A sync is running now.";
          })}
        />
      )}

      {showAhrefsForm && (
        <AhrefsKeyForm
          apiKey={ahrefsKey}
          domain={ahrefsDomain}
          busy={busy !== null}
          onApiKey={setAhrefsKey}
          onDomain={setAhrefsDomain}
          onSave={() => run("saveahrefs", async () => {
            await api.connectAhrefs(c.product_slug, ahrefsKey.trim(), ahrefsDomain.trim());
            setShowAhrefsForm(false);
            setAhrefsKey("");
            reload();
            return "Token stored (encrypted). The weekly job is the live test.";
          })}
        />
      )}

      {showPaidSocialForm && c.kind === "meta_ads" && (
        <MetaAdsConnectForm slug={c.product_slug} onDone={() => { setShowPaidSocialForm(false); reload(); followSync(reload); }} />
      )}
      {showPaidSocialForm && c.kind === "linkedin_ads" && (
        <LinkedinAdsConnectForm slug={c.product_slug} onDone={() => { setShowPaidSocialForm(false); reload(); followSync(reload); }} />
      )}
      {showPaidSocialForm && c.kind === "meta_social" && (
        <MetaSocialConnectForm slug={c.product_slug} onDone={() => { setShowPaidSocialForm(false); reload(); followSync(reload); }} />
      )}
      {showPaidSocialForm && c.kind === "x_social" && (
        <XSocialConnectForm slug={c.product_slug} onDone={() => { setShowPaidSocialForm(false); reload(); followSync(reload); }} />
      )}
      {showStripeForm && c.kind === "stripe" && (
        <StripeCredentialsForm slug={c.product_slug} onDone={() => { setShowStripeForm(false); reload(); followSync(reload); }} />
      )}

      {showMysqlForm && (
        <MysqlCredentialsForm slug={c.product_slug} onDone={() => { setShowMysqlForm(false); reload(); followSync(reload); }} />
      )}
      {showPostgresForm && (
        <PostgresCredentialsForm slug={c.product_slug} onDone={() => { setShowPostgresForm(false); reload(); followSync(reload); }} />
      )}
      {showMongoForm && (
        <MongoCredentialsForm slug={c.product_slug} onDone={() => { setShowMongoForm(false); reload(); followSync(reload); }} />
      )}

      {note && <div style={{ fontSize: 12.5, color: "var(--text-3)" }}>{note}</div>}
    </div>
  );
}

function MissingPanel({
  kind,
  slug,
  productName,
  reload,
}: {
  kind: string;
  slug: string;
  productName: string;
  reload: () => void;
}) {
  const meta = kindMeta(kind);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [showKeyForm, setShowKeyForm] = useState(false);
  const [showClarityForm, setShowClarityForm] = useState(false);
  const [showMysqlForm, setShowMysqlForm] = useState(false);
  const [showPostgresForm, setShowPostgresForm] = useState(false);
  const [showMongoForm, setShowMongoForm] = useState(false);
  const [key, setKey] = useState("");
  const [domain, setDomain] = useState("");
  const [clarityToken, setClarityToken] = useState("");
  const [clarityProject, setClarityProject] = useState("");
  const [showPosthogForm, setShowPosthogForm] = useState(false);
  const [posthogKey, setPosthogKey] = useState("");
  const [posthogProject, setPosthogProject] = useState("");
  const [posthogHost, setPosthogHost] = useState("https://us.posthog.com");
  const [showOpinlyForm, setShowOpinlyForm] = useState(false);
  const [opinlyKey, setOpinlyKey] = useState("");
  const [opinlyCompany, setOpinlyCompany] = useState("");
  const [showBingForm, setShowBingForm] = useState(false);
  const [bingKey, setBingKey] = useState("");
  const [bingSite, setBingSite] = useState("");
  const [showAhrefsForm, setShowAhrefsForm] = useState(false);
  const [ahrefsKey, setAhrefsKey] = useState("");
  const [ahrefsDomain, setAhrefsDomain] = useState("");
  const googleKind = googleOAuthKind(kind);
  const operatorOnly = false;

  const startGoogle = async () => {
    setBusy(true);
    setNote(null);
    try {
      const start = await api.connect.start(slug, [kind]);
      location.href = start.auth_url;
    } catch (e) {
      setNote(e instanceof ApiError ? e.message : String(e));
      setBusy(false);
    }
  };

  const saveKey = async () => {
    setBusy(true);
    setNote(null);
    try {
      await api.setSemrushKey(slug, key.trim(), domain.trim());
      setShowKeyForm(false);
      setKey("");
      setDomain("");
      setBusy(false);
      reload();
    } catch (e) {
      setNote(e instanceof ApiError ? e.message : String(e));
      setBusy(false);
    }
  };

  return (
    <div className="panel integration-card integration-card-missing">
      <div className="integration-card-head">
        <IntegrationMark kind={kind} />
        <div className="integration-card-heading">
          <span className="eyebrow">{productName || slug} · {meta.category}</span>
          <span className="integration-card-title">{meta.label}</span>
        </div>
        <StatusChip kind="pre-gate" label="not connected" />
      </div>

      <p className="integration-blurb">{meta.blurb}</p>
      <PullList pulls={meta.pulls} />

      <div className="integration-actions">
        {googleKind && (
          <div>
            <button type="button" className="btn-solid" disabled={busy} onClick={startGoogle}>
              {busy ? "Redirecting…" : "Connect with Google"}
            </button>
          </div>
        )}
        {googleSaKind(kind) && (
          <GrantAccessInstead
            slug={slug}
            kinds={[kind]}
            onDone={() => { reload(); followSync(reload); }}
          />
        )}
        {kind === "mysql" && !showMysqlForm && (
          <div>
            <button type="button" className="btn-solid" onClick={() => setShowMysqlForm(true)}>
              Connect database
            </button>
          </div>
        )}
        {kind === "mysql" && showMysqlForm && (
          <MysqlCredentialsForm slug={slug} onDone={() => { setShowMysqlForm(false); reload(); followSync(reload); }} />
        )}
        {kind === "postgres" && !showPostgresForm && (
          <div>
            <button type="button" className="btn-solid" onClick={() => setShowPostgresForm(true)}>
              Connect database
            </button>
          </div>
        )}
        {kind === "postgres" && showPostgresForm && (
          <PostgresCredentialsForm slug={slug} onDone={() => { setShowPostgresForm(false); reload(); followSync(reload); }} />
        )}
        {kind === "mongo" && !showMongoForm && (
          <div>
            <button type="button" className="btn-solid" onClick={() => setShowMongoForm(true)}>
              Connect database
            </button>
          </div>
        )}
        {kind === "mongo" && showMongoForm && (
          <MongoCredentialsForm slug={slug} onDone={() => { setShowMongoForm(false); reload(); followSync(reload); }} />
        )}
        {kind === "semrush" && !showKeyForm && (
          <div>
            <button type="button" className="btn-solid" onClick={() => setShowKeyForm(true)}>
              Set API key
            </button>
          </div>
        )}
        {kind === "semrush" && showKeyForm && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, maxWidth: 420 }}>
            <input className="input" type="password" placeholder="SEMrush API key"
              value={key} onChange={(e) => setKey(e.target.value)} />
            <input className="input" placeholder="Domain to track (example.com)"
              value={domain} onChange={(e) => setDomain(e.target.value)} />
            <div>
              <button type="button" className="btn-solid" disabled={!key || !domain || busy} onClick={saveKey}>
                {busy ? "Saving…" : "Save key"}
              </button>
            </div>
          </div>
        )}
        {kind === "clarity" && !showClarityForm && (
          <div>
            <button type="button" className="btn-solid" onClick={() => setShowClarityForm(true)}>
              Set API token
            </button>
          </div>
        )}
        {kind === "clarity" && showClarityForm && (
          <ClarityTokenForm
            projectId={clarityProject}
            token={clarityToken}
            busy={busy}
            onProjectId={setClarityProject}
            onToken={setClarityToken}
            onSave={async () => {
              setBusy(true);
              setNote(null);
              try {
                await api.connectClarity(slug, clarityToken.trim(), clarityProject.trim());
                setShowClarityForm(false);
                setClarityToken("");
                setClarityProject("");
                setBusy(false);
                reload();
              } catch (e) {
                setNote(e instanceof ApiError ? e.message : String(e));
                setBusy(false);
              }
            }}
          />
        )}
        {kind === "posthog" && !showPosthogForm && (
          <div>
            <button type="button" className="btn-solid" onClick={() => setShowPosthogForm(true)}>
              Set API key
            </button>
          </div>
        )}
        {kind === "posthog" && showPosthogForm && (
          <PosthogKeyForm
            projectId={posthogProject}
            apiKey={posthogKey}
            host={posthogHost}
            busy={busy}
            onProjectId={setPosthogProject}
            onApiKey={setPosthogKey}
            onHost={setPosthogHost}
            onSave={async () => {
              setBusy(true);
              setNote(null);
              try {
                await api.connectPosthog(
                  slug, posthogKey.trim(), posthogProject.trim(), posthogHost.trim() || undefined);
                setShowPosthogForm(false);
                setPosthogKey("");
                setPosthogProject("");
                setBusy(false);
                reload();
                followSync(reload);
              } catch (e) {
                setNote(e instanceof ApiError ? e.message : String(e));
                setBusy(false);
              }
            }}
          />
        )}
        {kind === "opinly" && !showOpinlyForm && (
          <div>
            <button type="button" className="btn-solid" onClick={() => setShowOpinlyForm(true)}>
              Set API key
            </button>
          </div>
        )}
        {kind === "opinly" && showOpinlyForm && (
          <OpinlyKeyForm
            apiKey={opinlyKey}
            companyId={opinlyCompany}
            busy={busy}
            onApiKey={setOpinlyKey}
            onCompanyId={setOpinlyCompany}
            onSave={async () => {
              setBusy(true);
              setNote(null);
              try {
                await api.connectOpinly(slug, opinlyKey.trim(), opinlyCompany.trim() || undefined);
                setShowOpinlyForm(false);
                setOpinlyKey("");
                setOpinlyCompany("");
                setBusy(false);
                reload();
                followSync(reload);
              } catch (e) {
                setNote(e instanceof ApiError ? e.message : String(e));
                setBusy(false);
              }
            }}
          />
        )}
        {kind === "bing" && !showBingForm && (
          <div>
            <button type="button" className="btn-solid" onClick={() => setShowBingForm(true)}>
              Set API key
            </button>
          </div>
        )}
        {kind === "bing" && showBingForm && (
          <BingKeyForm
            apiKey={bingKey}
            siteUrl={bingSite}
            busy={busy}
            onApiKey={setBingKey}
            onSiteUrl={setBingSite}
            onSave={async () => {
              setBusy(true);
              setNote(null);
              try {
                await api.connectBing(slug, bingKey.trim(), bingSite.trim());
                setShowBingForm(false);
                setBingKey("");
                setBingSite("");
                setBusy(false);
                reload();
                followSync(reload);
              } catch (e) {
                setNote(e instanceof ApiError ? e.message : String(e));
                setBusy(false);
              }
            }}
          />
        )}
        {kind === "ahrefs" && !showAhrefsForm && (
          <div>
            <button type="button" className="btn-solid" onClick={() => setShowAhrefsForm(true)}>
              Set API token
            </button>
          </div>
        )}
        {kind === "ahrefs" && showAhrefsForm && (
          <AhrefsKeyForm
            apiKey={ahrefsKey}
            domain={ahrefsDomain}
            busy={busy}
            onApiKey={setAhrefsKey}
            onDomain={setAhrefsDomain}
            onSave={async () => {
              setBusy(true);
              setNote(null);
              try {
                await api.connectAhrefs(slug, ahrefsKey.trim(), ahrefsDomain.trim());
                setShowAhrefsForm(false);
                setAhrefsKey("");
                setAhrefsDomain("");
                setBusy(false);
                reload();
              } catch (e) {
                setNote(e instanceof ApiError ? e.message : String(e));
                setBusy(false);
              }
            }}
          />
        )}
        {kind === "meta_ads" && (
          <MetaAdsConnectForm slug={slug} onDone={() => { reload(); followSync(reload); }} />
        )}
        {kind === "linkedin_ads" && (
          <LinkedinAdsConnectForm slug={slug} onDone={() => { reload(); followSync(reload); }} />
        )}
        {kind === "meta_social" && (
          <MetaSocialConnectForm slug={slug} onDone={() => { reload(); followSync(reload); }} />
        )}
        {kind === "x_social" && (
          <XSocialConnectForm slug={slug} onDone={() => { reload(); followSync(reload); }} />
        )}
        {kind === "stripe" && (
          <StripeCredentialsForm slug={slug} onDone={() => { reload(); followSync(reload); }} />
        )}
        {operatorOnly && (
          <div style={{ fontSize: 12.5, color: "var(--text-4)", lineHeight: 1.6 }}>
            Operator-managed source: wired via a connection row + env credentials (README §Wiring).
          </div>
        )}
        {note && <div className="form-error">{note}</div>}
      </div>
    </div>
  );
}

function ClarityTokenForm({
  projectId, token, busy, onProjectId, onToken, onSave,
}: {
  projectId: string;
  token: string;
  busy: boolean;
  onProjectId: (v: string) => void;
  onToken: (v: string) => void;
  onSave: () => void;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, maxWidth: 420 }}>
      <input className="input" type="password" placeholder="Clarity Data Export token"
        value={token} onChange={(e) => onToken(e.target.value)}
        aria-label="Clarity Data Export token" />
      <input className="input" placeholder="Project id (Settings → Overview)"
        value={projectId} onChange={(e) => onProjectId(e.target.value)}
        aria-label="Clarity project id" />
      <div style={{ fontSize: 12, color: "var(--text-faint)", lineHeight: 1.5 }}>
        Generate the token in Clarity → Settings → Data Export. The daily job
        stores playback links, not video.
      </div>
      <div>
        <button type="button" className="btn-solid"
          disabled={!token.trim() || !projectId.trim() || busy} onClick={onSave}>
          {busy ? "Saving…" : "Save token"}
        </button>
      </div>
    </div>
  );
}

function BingKeyForm({
  apiKey, siteUrl, busy, onApiKey, onSiteUrl, onSave,
}: {
  apiKey: string;
  siteUrl: string;
  busy: boolean;
  onApiKey: (v: string) => void;
  onSiteUrl: (v: string) => void;
  onSave: () => void;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, maxWidth: 420 }}>
      <input className="input" type="password" placeholder="Bing Webmaster API key"
        value={apiKey} onChange={(e) => onApiKey(e.target.value)}
        aria-label="Bing Webmaster API key" />
      <input className="input" placeholder="Site URL (https://example.com)"
        value={siteUrl} onChange={(e) => onSiteUrl(e.target.value)}
        aria-label="Bing Webmaster site URL" />
      <div style={{ fontSize: 12, color: "var(--text-faint)", lineHeight: 1.5 }}>
        Generate the key in Bing Webmaster → Settings → API access. Use the
        site URL exactly as it appears in the portal.
      </div>
      <div>
        <button type="button" className="btn-solid"
          disabled={!apiKey.trim() || !siteUrl.trim() || busy} onClick={onSave}>
          {busy ? "Saving…" : "Save key"}
        </button>
      </div>
    </div>
  );
}

function AhrefsKeyForm({
  apiKey, domain, busy, onApiKey, onDomain, onSave,
}: {
  apiKey: string;
  domain: string;
  busy: boolean;
  onApiKey: (v: string) => void;
  onDomain: (v: string) => void;
  onSave: () => void;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, maxWidth: 420 }}>
      <input className="input" type="password" placeholder="Ahrefs API v3 token"
        value={apiKey} onChange={(e) => onApiKey(e.target.value)}
        aria-label="Ahrefs API token" />
      <input className="input" placeholder="Domain to track (example.com)"
        value={domain} onChange={(e) => onDomain(e.target.value)}
        aria-label="Ahrefs target domain" />
      <div style={{ fontSize: 12, color: "var(--text-faint)", lineHeight: 1.5 }}>
        Create a v3 token in Ahrefs → Account → API. Snapshots stay weekly —
        units deplete, so there is no Sync now.
      </div>
      <div>
        <button type="button" className="btn-solid"
          disabled={!apiKey.trim() || !domain.trim() || busy} onClick={onSave}>
          {busy ? "Saving…" : "Save token"}
        </button>
      </div>
    </div>
  );
}

function OpinlyKeyForm({
  apiKey, companyId, busy, onApiKey, onCompanyId, onSave,
}: {
  apiKey: string;
  companyId: string;
  busy: boolean;
  onApiKey: (v: string) => void;
  onCompanyId: (v: string) => void;
  onSave: () => void;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, maxWidth: 420 }}>
      <input className="input" type="password" placeholder="Opinly API key (sk-…)"
        value={apiKey} onChange={(e) => onApiKey(e.target.value)}
        aria-label="Opinly API key" />
      <input className="input" placeholder="Company id (comp_…, optional if you have one)"
        value={companyId} onChange={(e) => onCompanyId(e.target.value)}
        aria-label="Opinly company id" />
      <div style={{ fontSize: 12, color: "var(--text-faint)", lineHeight: 1.5 }}>
        Create the key in Opinly → Settings → Developers. The daily job snapshots
        AI-search visibility, keyword ranks and site-audit health. Pass a company
        id if the key can see more than one workspace.
      </div>
      <div>
        <button type="button" className="btn-solid"
          disabled={!apiKey.trim() || busy} onClick={onSave}>
          {busy ? "Saving…" : "Save key"}
        </button>
      </div>
    </div>
  );
}

function PosthogKeyForm({
  projectId, apiKey, host, busy, onProjectId, onApiKey, onHost, onSave,
}: {
  projectId: string;
  apiKey: string;
  host: string;
  busy: boolean;
  onProjectId: (v: string) => void;
  onApiKey: (v: string) => void;
  onHost: (v: string) => void;
  onSave: () => void;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, maxWidth: 420 }}>
      <input className="input" type="password" placeholder="Personal API key (phx_…)"
        value={apiKey} onChange={(e) => onApiKey(e.target.value)}
        aria-label="PostHog personal API key" />
      <input className="input" placeholder="Project id (the number in your project URL)"
        value={projectId} onChange={(e) => onProjectId(e.target.value)}
        aria-label="PostHog project id" />
      <input className="input" list="posthog-hosts" placeholder="https://us.posthog.com"
        value={host} onChange={(e) => onHost(e.target.value)} aria-label="PostHog host" />
      <datalist id="posthog-hosts">
        <option value="https://us.posthog.com" />
        <option value="https://eu.posthog.com" />
      </datalist>
      <div style={{ fontSize: 12, color: "var(--text-faint)", lineHeight: 1.5 }}>
        Create the key in PostHog → Settings → Personal API keys, scoped to{" "}
        <code>query:read</code> for funnel events and <code>session_recording:read</code> for
        replay. Leave the host as US cloud unless you are on EU cloud or self-hosted. Map events
        onto stages after saving.
      </div>
      <div>
        <button type="button" className="btn-solid"
          disabled={!apiKey.trim() || !projectId.trim() || busy} onClick={onSave}>
          {busy ? "Saving…" : "Save key"}
        </button>
      </div>
    </div>
  );
}

function ConfigView({ config }: { config: Record<string, unknown> | null }) {
  const entries = Object.entries(config ?? {});
  if (entries.length === 0)
    return <span style={{ color: "var(--text-faint)" }}>empty</span>;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {entries.map(([k, v]) => (
        <div key={k} className="num" style={{ color: "var(--text-2)", wordBreak: "break-word" }}>
          <span style={{ color: "var(--text-faint)" }}>{k}: </span>
          {summarise(v)}
        </div>
      ))}
    </div>
  );
}

function summarise(v: unknown): string {
  if (v === null || v === undefined) return "null";
  if (Array.isArray(v)) return v.length === 0 ? "[] (empty)" : `${v.length} entr${v.length === 1 ? "y" : "ies"}`;
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

/**
 * How much history this source has read so far. A source climbs a ladder — 30
 * days, then a year, then everything the plan allows — so a shallow number on
 * a fresh connection means "still filling in", not "that's all there is".
 */
function historyDepth(c: Connection): string | null {
  const days = c.backfill_days ?? 0;
  if (days <= 0) return null;
  if (days >= 3650) return "full history";
  if (days >= 365) return `${Math.round(days / 365)}y of history`;
  return `${days}d of history`;
}

function statusChip(status: Connection["status"]): ChipKind {
  if (status === "active") return "passing";
  if (status === "error") return "failing";
  if (status === "disabled") return "killed";
  return "pre-gate";
}

/** What actually has to happen next for this connection to deliver data. */
function nextStep(c: Connection): ReactNode {
  const cfg = c.config ?? {};

  if (c.kind === "mysql") {
    const maps = Array.isArray(cfg.event_maps) ? cfg.event_maps.length : 0;
    const envPath = Boolean(cfg.host_env) && !c.credential_id;
    if (!c.credential_id && !envPath)
      return <>No credentials yet — Set credentials below (a read-only user, ideally on a replica).</>;
    if (maps === 0)
      return (
        <>
          Credentials {envPath ? "resolve from env vars" : "are set"}, but no event maps — nothing will
          sync until tables are mapped. Use <strong>Suggest mappings</strong> below (a common template
          or AI), review, then Save — or set them via <code>set_mysql_event_maps</code>.
        </>
      );
    return envPath
      ? <>{maps} event map(s) configured. Confirm the named env vars are set on the worker.</>
      : null;
  }
  if (c.kind === "postgres" || c.kind === "mongo") {
    const maps = Array.isArray(cfg.event_maps) ? cfg.event_maps.length : 0;
    const unit = c.kind === "mongo" ? "collection" : "table";
    if (!c.credential_id)
      return <>No credentials yet — {c.kind === "mongo" ? "set the connection string" : "set credentials"} below (read-only, ideally on a replica).</>;
    if (maps === 0)
      return (
        <>
          Credentials are set, but no event maps — nothing will sync until {unit}s are mapped. Use{" "}
          <strong>Event maps → Edit</strong> below (or <code>set_{c.kind}_event_maps</code> via MCP/API).
        </>
      );
    return null;
  }
  if (c.kind === "gsc" && !cfg.site_url)
    return <>No Search Console site selected yet — Grant access (or Connect with Google) and pick the property.</>;
  if (c.kind === "ga4" && !cfg.property_id)
    return <>No property selected yet — finish the Google connect flow (Connect, then pick the GA4 property).</>;
  // Without a container there is nothing to audit and nothing to publish into,
  // and the generic "flip status to active" line below sends people looking for
  // a switch that does not exist.
  if (c.kind === "gtm" && !cfg.container_path)
    return <>No container selected yet — finish the Google connect flow (Connect, then pick the GTM container).</>;
  if (c.kind === "meta_ads" && !cfg.ad_account_id)
    return <>No ad account selected yet — paste a Meta token and pick the account.</>;
  if (c.kind === "linkedin_ads" && !cfg.ad_account_id)
    return <>No LinkedIn ad account selected yet — paste a token and pick the account.</>;
  if (c.kind === "meta_social" && !cfg.page_id)
    return <>No Facebook Page selected yet — paste a token and pick the Page.</>;
  if (c.kind === "x_social" && !cfg.username)
    return <>No X username set — paste a Bearer token and the handle.</>;
  if (c.kind === "stripe" && !c.credential_id)
    return <>No key yet — paste a Restricted key (Charges, Customers, Refunds, Disputes read).</>;
  if (c.kind === "google_ads" && !cfg.customer_id)
    return (
      <>
        Connect with Google, then choose the Ads account. Until then push spend through{" "}
        <code>POST /import/google-spend</code>.
      </>
    );
  if (c.kind === "google_ads" && c.status === "pending")
    return (
      <>
        Account stored. Test the connection to activate spend sync, then add the
        landing-URL parameters below so keyword and creative ROI can be exact.
      </>
    );
  if (c.kind === "bing") {
    if (!c.credential_id)
      return <>No API key yet — paste the key from Bing Webmaster → Settings → API access.</>;
    if (!(c.config?.site_url))
      return <>Key stored, but no site URL — queries cannot sync until one is set.</>;
    return null;
  }
  if (c.kind === "ahrefs") {
    if (!c.credential_id)
      return <>No token yet — paste an Ahrefs API v3 token and the domain to track.</>;
    if (!(c.config?.domain))
      return <>Token stored, but no domain — the weekly snapshot cannot run until one is set.</>;
    return <>Snapshots stay weekly — units deplete. There is no Sync now.</>;
  }
  if (c.kind === "semrush" && c.status !== "active")
    return (
      <>
        Set <code>SEMRUSH_API_KEY</code> and flip status to <code>active</code>. Snapshots stay
        weekly — units deplete.
      </>
    );
  if (c.kind === "clarity" && !c.credential_id)
    return <>Paste the Data Export token and project id from Clarity → Settings.</>;
  if (c.kind === "clarity" && !(c.config?.project_id))
    return <>Token stored, but no project id — recordings cannot be linked until one is set.</>;
  if (c.kind === "opinly") {
    if (!c.credential_id)
      return <>No API key yet — paste the sk- key from Opinly → Settings → Developers.</>;
    if (!(c.config?.company_id))
      return <>Key stored, but no company id — the snapshot cannot run until one is set.</>;
    return null;
  }
  if (c.kind === "posthog") {
    if (!c.credential_id)
      return <>No API key yet — paste a personal API key scoped to <code>query:read</code>.</>;
    const maps = Array.isArray(cfg.event_maps) ? cfg.event_maps.length : 0;
    if (maps === 0)
      return (
        <>
          Key stored, but no event maps — the funnel stays empty until PostHog events are mapped
          onto stages. Use <strong>Event mapping → Edit</strong> below.
        </>
      );
    if (cfg.traffic_owner === "ga4")
      return (
        <>
          {maps} event map(s) active. GA4 owns visit and engaged traffic for this product, so
          PostHog contributes mapped events and session replay only — counting sessions twice
          would double them.
        </>
      );
    return null;
  }
  if (c.status === "pending")
    return <>Credentials set? Flip status to <code>active</code> to include it in the sweep.</>;
  return null;
}

function Loading() {
  return (
    <div className="page" style={{ padding: "60px 40px", color: "var(--text-faint)" }}>
      Loading…
    </div>
  );
}

function Problem({ message }: { message: string }) {
  return (
    <div className="page" style={{ padding: "60px 40px", color: "var(--neg)" }}>
      {message}
    </div>
  );
}
